import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Activity, Target, AlertTriangle, Trophy, ArrowRight, Shield, CheckCircle, Bell, AlertCircle, TrendingUp, FileWarning, Zap, ChevronRight, Clock, Users, Calendar, FileText, Flame } from 'lucide-react';
import ActionItemButtons from '../components/dashboard/ActionItemButtons';
import useOfficerUtils from '../components/hooks/useOfficerUtils';
import AntiPatternDetector from '@/components/dashboard/AntiPatternDetector';


const TAG_FIELD_BY_TYPE = {
  officer: "tagged_officers",
  artifact: "tagged_artifacts",
  ritual: "tagged_rituals",
  policy: "tagged_policies",
  capability: "tagged_capabilities"
};

function getTagField(tagType) {
  const field = TAG_FIELD_BY_TYPE[tagType];
  if (!field) {
    console.error("Unknown tag type:", tagType);
    return null;
  }
  return field;
}
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import VisualTeamMap from '../components/dashboard/VisualTeamMap';
import ActivityPrinciplesValidation from '../components/commander/ActivityPrinciplesValidation';
import ProgressReportPrinciplesValidation from '../components/commander/ProgressReportPrinciplesValidation';
import DailyTaskPrinciplesValidation from '../components/commander/DailyTaskPrinciplesValidation';
import MetricCard from '../components/dashboard/MetricCard';
import ActionItemsDialog from '../components/dashboard/ActionItemsDialog';
import OpenIssuesDialog from '../components/dashboard/OpenIssuesDialog';
import StrugglingPrinciplesDialog from '../components/dashboard/StrugglingPrinciplesDialog';
import TaggedItemsDialog from '../components/dashboard/TaggedItemsDialog';
import TagDetailsDialog from '../components/dashboard/TagDetailsDialog';
import StrategyTacticsDialog from '../components/dashboard/StrategyTacticsDialog';
import IssueTagsCard from '../components/dashboard/IssueTagsCard';
import IssuesByHierarchyCard from '../components/dashboard/IssuesByHierarchyCard';
import TagHistoryDialog from '../components/activities/TagHistoryDialog';
import PendingBotApprovalsCard from '../components/dashboard/PendingBotApprovalsCard';
import BotsManagementSection from '../components/dashboard/BotsManagementSection';
import HighCouncilMetrics from '../components/dashboard/HighCouncilMetrics';

export default function Dashboard() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('all');
  const [validatingActivity, setValidatingActivity] = useState(null);
  const [validatingReport, setValidatingReport] = useState({ activity: null, reportIndex: null });
  const [validatingTasks, setValidatingTasks] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    activations: false,
    approvals: false,
    reportsWaitingApproval: false,
    validations: false,
    issues: false,
    missions: false,
    noReports: false,
    // New sections
    criticalMissions: false,
    criticalObjectives: false,
    emptyObjectives: false,
    criticalActivities: false,
    overdueTasks: false,
    criticalTasks: false,
    tentativeAllocatedByMe: false,
    tentativeAllocatedToMe: false,
    policies: false,
    capabilities: false
  });
  const [expandedCategories, setExpandedCategories] = useState({
    missionCommand: false,
    objectiveDeployment: false,
    activityOperations: false,
    dailyTaskDirectives: false,
    systemGovernance: false
  });
  const [showActionItemsDialog, setShowActionItemsDialog] = useState(false);
  const [showOpenIssuesDialog, setShowOpenIssuesDialog] = useState(false);
  const [showStrugglingPrinciplesDialog, setShowStrugglingPrinciplesDialog] = useState(false);
  const [showStrategyTacticsDialog, setShowStrategyTacticsDialog] = useState(false);

  const [selectedTagType, setSelectedTagType] = useState(null);
  const [viewingOfficerEmail, setViewingOfficerEmail] = useState(null);
  const [tagHistoryDialog, setTagHistoryDialog] = useState({ open: false, tag: null });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  const { data: user } = useQuery({
    queryKey: ['me'],
    queryFn: () => base44.auth.me()
  });

  // Use viewing officer email if commander is viewing someone else's dashboard
  const effectiveUserEmail = viewingOfficerEmail || user?.email;
  const viewingOwnDashboard = !viewingOfficerEmail || viewingOfficerEmail === user?.email;

  const { data: profile } = useQuery({
    queryKey: ['myProfile', effectiveUserEmail],
    queryFn: async () => {
      if (!effectiveUserEmail) return null;
      const profiles = await base44.entities.OfficerProfile.list();
      return profiles.find((p) => p.user_email === effectiveUserEmail) || {
        callsign: effectiveUserEmail?.split('@')[0] || "Officer",
        rank_title: "Captain",
        total_xp: 0,
        badges: []
      };
    },
    enabled: !!effectiveUserEmail
  });

  const isCommander = user?.role === 'admin' || profile?.custom_role === 'Commander';

  const { data: missions } = useQuery({
    queryKey: ['activeMissions'],
    queryFn: () => base44.entities.Mission.list(),
    initialData: []
  });

  const { data: pendingObjectives } = useQuery({
    queryKey: ['pendingObjectives', effectiveUserEmail],
    queryFn: async () => {
      const allObjectives = await base44.entities.Objective.list();
      return allObjectives.filter((obj) => {
        if (obj.status !== 'pending_activation') return false;

        // Show if user requested it
        if (obj.activation_requested_by === effectiveUserEmail) return true;

        // Show if user is sponsor/owner of the mission
        const mission = missions.find((m) => m.id === obj.mission_id);
        if (!mission) return false;
        return mission.sponsor_email === effectiveUserEmail || mission.owner_email === effectiveUserEmail;
      });
    },
    enabled: !!effectiveUserEmail && missions.length > 0,
    initialData: []
  });

  const { data: allObjectives } = useQuery({
    queryKey: ['allObjectives', effectiveUserEmail, isCommander, viewingOwnDashboard],
    queryFn: async () => {
      const objectives = await base44.entities.Objective.list();

      // Commanders viewing their own dashboard see everything
      if (isCommander && viewingOwnDashboard) return objectives;

      // Others (or commanders viewing other officers) only see objectives tied to their missions
      return objectives.filter((obj) => {
        if (obj.owner_email === effectiveUserEmail) return true;

        const mission = missions.find((m) => m.id === obj.mission_id);
        if (!mission) return false;
        return mission.sponsor_email === effectiveUserEmail ||
        mission.owner_email === effectiveUserEmail;
      });
    },
    enabled: !!effectiveUserEmail && missions.length > 0,
    initialData: []
  });

  const { data: officers } = useQuery({
    queryKey: ['officers'],
    queryFn: async () => {
      const profiles = await base44.entities.OfficerProfile.list();
      return profiles;
    },
    initialData: []
  });

  const { data: activities } = useQuery({
    queryKey: ['activities'],
    queryFn: () => base44.entities.Activity.list(),
    enabled: isCommander,
    initialData: []
  });

  const { data: dailyTasks } = useQuery({
    queryKey: ['dailyTasks'],
    queryFn: () => base44.entities.DailyTask.list(),
    initialData: []
  });

  const { data: progressReports } = useQuery({
    queryKey: ['progressReports'],
    queryFn: () => base44.entities.ProgressReport.list(),
    enabled: isCommander,
    initialData: []
  });

  const { data: issues } = useQuery({
    queryKey: ['issues'],
    queryFn: () => base44.entities.Issue.list(),
    initialData: []
  });

  const { data: reportIssues } = useQuery({
    queryKey: ['reportIssues'],
    queryFn: () => base44.entities.ReportIssue.list(),
    initialData: []
  });

  const { data: trainingRequests } = useQuery({
    queryKey: ['trainingRequests'],
    queryFn: () => base44.entities.TrainingRequest.list(),
    enabled: isCommander,
    initialData: []
  });

  const { data: artifacts } = useQuery({
    queryKey: ['artifacts'],
    queryFn: () => base44.entities.Artifact.list(),
    initialData: []
  });

  const { data: rituals } = useQuery({
    queryKey: ['rituals'],
    queryFn: () => base44.entities.Ritual.list(),
    initialData: []
  });

  const { data: policies } = useQuery({
    queryKey: ['policies'],
    queryFn: () => base44.entities.Policy.list(),
    initialData: []
  });

  const { data: capabilities } = useQuery({
    queryKey: ['capabilities'],
    queryFn: () => base44.entities.Capability.list(),
    initialData: []
  });

  const { data: councilSessions } = useQuery({
    queryKey: ['councilSessions'],
    queryFn: () => base44.entities.CouncilSession.list(),
    initialData: []
  });

  const { data: dismissals } = useQuery({
    queryKey: ['actionDismissals', effectiveUserEmail],
    queryFn: async () => {
      const all = await base44.entities.ActionItemDismissal.list();
      return all.filter(d => d.user_email === effectiveUserEmail);
    },
    enabled: !!effectiveUserEmail,
    initialData: []
  });

  const { data: strategicNodes } = useQuery({
    queryKey: ['strategicNodes'],
    queryFn: () => base44.entities.StrategicNode.list(),
    initialData: []
  });

  const { data: immutables } = useQuery({
    queryKey: ['immutables'],
    queryFn: () => base44.entities.Immutable.list(),
    initialData: []
  });

  // Use officer utils hook for consistent officer display
  const { getOfficerDisplay } = useOfficerUtils({
    officers,
    currentUserEmail: effectiveUserEmail,
    currentUserRole: user?.role
  });

  // Helper function to check if item is dismissed or snoozed
  const isItemDismissedOrSnoozed = (itemType, itemId) => {
    const dismissal = dismissals.find(d => d.item_type === itemType && d.item_id === itemId);
    if (!dismissal) return false;
    if (dismissal.action === 'dismissed') return true;
    if (dismissal.action === 'snoozed' && dismissal.snooze_until) {
      return new Date(dismissal.snooze_until) > new Date();
    }
    return false;
  };

  // Calculate all action items with relevance filtering
  const now = new Date();
  const fiveDaysAgo = new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000);
  const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);

  // 1. Objectives Pending Activation (commanders see all, others see only relevant ones)
  const relevantPendingObjectives = allObjectives.filter((obj) => {
    if (obj.status !== 'pending_activation') return false;
    if (isItemDismissedOrSnoozed('pending_objective', obj.id)) return false;

    // Commanders see all pending activations
    if (isCommander) return true;

    // Others only see if they requested it or are sponsor/owner of parent mission
    if (obj.activation_requested_by === user?.email) return true;
    const mission = missions.find((m) => m.id === obj.mission_id);
    return mission && (mission.sponsor_email === user?.email || mission.owner_email === user?.email);
  });

  // 2. Activities with Stale Progress Reports (5+ days, commanders see all, others see only if responsible/accountable)
  const activitiesWithStaleReports = activities.filter((a) => {
    if (a.status === 'completed' || a.status === 'archived') return false;
    if (isItemDismissedOrSnoozed('stale_report', a.id)) return false;
    if (!isCommander && a.responsible_email !== effectiveUserEmail && a.accountable_email !== effectiveUserEmail) return false;
    const reports = progressReports.filter((r) => r.activity_id === a.id);
    if (reports.length === 0) return false;
    const lastReport = reports.sort((a, b) => new Date(b.reported_at) - new Date(a.reported_at))[0];
    return new Date(lastReport.reported_at) < fiveDaysAgo;
  });

  // 3. Activities with No Progress Reports (commanders see all, others see only if responsible/accountable)
  const activitiesWithNoReports = activities.filter((a) => {
    if (a.status === 'completed' || a.status === 'archived') return false;
    if (isItemDismissedOrSnoozed('no_report', a.id)) return false;
    if (!isCommander && a.responsible_email !== effectiveUserEmail && a.accountable_email !== effectiveUserEmail) return false;
    const reports = progressReports.filter((r) => r.activity_id === a.id);
    return reports.length === 0;
  });

  // Progress Reports Pending Approval - Activities in pending_review status
  const pendingApprovalActivities = activities.filter((a) =>
  a.status === 'pending_review' && a.accountable_email === effectiveUserEmail &&
  !isItemDismissedOrSnoozed('pending_approval', a.id)
  );

  // Activities with progress reports waiting for approval
  const activitiesWithReportsWaitingApproval = activities.filter((a) => {
    if (a.accountable_email !== effectiveUserEmail) return false;
    if (isItemDismissedOrSnoozed('report_waiting_approval', a.id)) return false;
    const reports = progressReports.filter((r) => r.activity_id === a.id && !r.approved);
    return reports.length > 0;
  });

  // Pending Principles Validations (commander only viewing own dashboard) - Reports that need validation
  // Group by activity to avoid duplicates, take the most recent report
  const pendingValidationReports = isCommander && viewingOwnDashboard ? (() => {
    const uniqueActivities = new Map();
    progressReports.
    filter((report) =>
    report.approved && (!report.principles_validation?.status || report.principles_validation?.status === 'pending')
    ).
    sort((a, b) => new Date(b.reported_at) - new Date(a.reported_at)) // Sort by most recent first
    .forEach((report) => {
      const activity = activities.find((a) => a.id === report.activity_id);
      if (activity && !uniqueActivities.has(activity.id)) {
        uniqueActivities.set(activity.id, { activity, report, reportIndex: 0 });
      }
    });
    return Array.from(uniqueActivities.values());
  })() : [];

  // 7. Unresolved Issues (relevant if user is reporter or in related_officers)
  const unresolvedIssues = issues.filter((i) => {
    if (i.status !== 'open' && i.status !== 'under_review') return false;
    return i.reported_by === effectiveUserEmail || i.related_officers?.includes(effectiveUserEmail);
  });

  // 8. Pending Training Requests (commander only viewing own dashboard)
  const pendingTrainingRequests = isCommander && viewingOwnDashboard ? trainingRequests.filter((tr) => tr.status === 'pending') : [];

  // 9. Overdue Activities (relevant if user is responsible or accountable)
  const overdueActivities = activities.filter((a) => {
    if (a.status === 'completed' || a.status === 'archived' || !a.due_date) return false;
    if (a.responsible_email !== effectiveUserEmail && a.accountable_email !== effectiveUserEmail) return false;
    return new Date(a.due_date) < now;
  });

  // 10. High-Severity Unaddressed Issues (relevant if user is reporter or in related_officers)
  const highSeverityUnaddressedIssues = issues.filter((i) => {
    if (!['critical', 'high'].includes(i.severity)) return false;
    if (i.status !== 'open' && i.status !== 'under_review') return false;
    if (i.reported_by !== effectiveUserEmail && !i.related_officers?.includes(effectiveUserEmail)) return false;
    return !i.linked_objective_id && !i.linked_policy_id && !i.linked_capability_id;
  });

  // 11. Unassigned Missions (relevant if user is sponsor or owner)
  const unassignedMissions = missions.filter((m) => {
    if (m.status === 'archived' || m.status === 'completed') return false;
    const missingLeadership = !m.sponsor_email || !m.owner_email;
    if (!missingLeadership) return false;
    if (isItemDismissedOrSnoozed('unassigned_mission', m.id)) return false;
    return isCommander || m.sponsor_email === effectiveUserEmail || m.owner_email === effectiveUserEmail;
  });

  // 12. Stale Artifacts/Rituals Pending Approval (relevant if user is owner or commander)
  const staleArtifactsPendingApproval = artifacts.filter((a) => {
    if (!['draft', 'pending_approval'].includes(a.status)) return false;
    if (!isCommander && a.owner_email !== effectiveUserEmail) return false;
    return new Date(a.created_date) < twoWeeksAgo;
  });

  const staleRitualsPendingApproval = rituals.filter((r) => {
    if (!['draft', 'pending_approval'].includes(r.status)) return false;
    if (!isCommander && r.owner_email !== effectiveUserEmail) return false;
    return new Date(r.created_date) < twoWeeksAgo;
  });

  // 13. Overdue Artifact/Ritual Audits (relevant if user is owner or commander)
  const overdueArtifactAudits = artifacts.filter((a) => {
    if (!isCommander && a.owner_email !== effectiveUserEmail) return false;
    if (!a.last_audit_date) return new Date(a.created_date) < new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
    return new Date(a.last_audit_date) < new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
  });

  const overdueRitualAudits = rituals.filter((r) => {
    if (!isCommander && r.owner_email !== effectiveUserEmail) return false;
    if (!r.last_audit_date) return new Date(r.created_date) < new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
    return new Date(r.last_audit_date) < new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
  });

  const activeMissions = missions.filter((m) => m.status === 'active');
  const activeObjectives = allObjectives.filter((o) => o.status === 'active');
  const activeActivities = activities.filter((a) => a.status === 'active');
  const activeTasks = dailyTasks.filter((t) => !t.completed);

  // New Filters for Hierarchical Action Items

  // I. Mission Command
  const missionsAwaitingApproval = missions.filter((m) =>
    m.status === 'briefing' &&
    (m.sponsor_email === effectiveUserEmail || m.owner_email === effectiveUserEmail || isCommander) &&
    !isItemDismissedOrSnoozed('mission_awaiting_approval', m.id)
  );

  const criticalMissions = activeMissions.filter((m) =>
  m.status_flag === 'red' && (
  m.sponsor_email === effectiveUserEmail || m.owner_email === effectiveUserEmail) &&
  !isItemDismissedOrSnoozed('critical_mission', m.id)
  );

  // II. Objective Deployment
  const criticalObjectives = activeObjectives.filter((o) =>
  o.status_flag === 'red' &&
  o.owner_email === effectiveUserEmail &&
  !isItemDismissedOrSnoozed('critical_objective', o.id)
  );

  const objectivesWithoutActivities = activeObjectives.filter((o) =>
  o.owner_email === effectiveUserEmail &&
  !activities.some((a) => a.objective_id === o.id && a.status !== 'archived') &&
  !isItemDismissedOrSnoozed('empty_objective', o.id)
  );

  // III. Activity Operations
  const criticalActivities = activeActivities.filter((a) =>
  a.key_results_status === 'red' && (
  a.responsible_email === effectiveUserEmail || a.accountable_email === effectiveUserEmail) &&
  !isItemDismissedOrSnoozed('critical_activity', a.id)
  );

  // IV. Daily Tasks (ensure we filter by user)
  const myActiveTasks = activeTasks.filter((t) => t.user_email === effectiveUserEmail);

  const overdueDailyTasks = myActiveTasks.filter((t) =>
  new Date(t.date) < new Date(now.getFullYear(), now.getMonth(), now.getDate()) &&
  !isItemDismissedOrSnoozed('overdue_task', t.id)
  );

  const criticalDailyTasks = myActiveTasks.filter((t) =>
  t.status_flag === 'red' &&
  !isItemDismissedOrSnoozed('critical_task', t.id)
  );

  // Tentative tasks allocated by me to others
  const tentativeTasksAllocatedByMe = dailyTasks.filter((t) =>
    t.status === 'tentative' &&
    t.created_by_email === effectiveUserEmail &&
    t.user_email !== effectiveUserEmail &&
    !isItemDismissedOrSnoozed('tentative_by_me', t.id)
  );

  // Tentative tasks allocated to me by others
  const tentativeTasksAllocatedToMe = dailyTasks.filter((t) =>
    t.status === 'tentative' &&
    t.user_email === effectiveUserEmail &&
    t.created_by_email !== effectiveUserEmail &&
    !isItemDismissedOrSnoozed('tentative_to_me', t.id)
  );

  // Tentative tasks awaiting my approval (as accountable/responsible)
  const tentativeTasksAwaitingMyApproval = dailyTasks.filter((t) => {
    if (t.status !== 'tentative') return false;
    if (t.created_by_email === effectiveUserEmail) return false; // Don't show my own tasks
    if (isItemDismissedOrSnoozed('tentative_awaiting_approval', t.id)) return false;
    
    const activity = activities.find(a => a.id === t.activity_id);
    if (!activity) return false;
    
    // Show if I'm accountable or responsible for the linked activity
    return activity.accountable_email === effectiveUserEmail || activity.responsible_email === effectiveUserEmail;
  });

  // Bridging Policies Overdue for Review
  const bridgingPoliciesOverdue = policies.filter(p => 
    p.interim_for_capability_development && p.review_date && new Date(p.review_date) < now && p.status !== 'retired'
  );

  // V. System & Governance
  const policiesPendingReview = policies.filter((p) =>
  (p.status === 'draft' || p.status === 'pending_approval' || p.status === 'under_review') && (
  isCommander || p.designed_by === effectiveUserEmail)
  );

  const capabilitiesRequested = capabilities.filter((c) =>
  (c.status === 'requested' || c.status === 'in_development') && (
  isCommander || c.requested_by === effectiveUserEmail)
  );

  // New: Artifacts/Rituals pending approval (draft or pending_approval)
  const artifactsPendingApproval = artifacts.filter((a) =>
    (a.status === 'draft' || a.status === 'pending_approval') && (isCommander || a.owner_email === effectiveUserEmail)
  );

  const ritualsPendingApproval = rituals.filter((r) =>
    (r.status === 'draft' || r.status === 'pending_approval') && (isCommander || r.owner_email === effectiveUserEmail)
  );

  // New: Approved Artifacts/Rituals pending institutionalization
  const artifactsPendingInstitutionalization = artifacts.filter((a) =>
    a.status === 'approved' && (isCommander || a.owner_email === effectiveUserEmail)
  );

  const ritualsPendingInstitutionalization = rituals.filter((r) =>
    r.status === 'approved' && (isCommander || r.owner_email === effectiveUserEmail)
  );

  // New: Approved Policies/Capabilities pending institutionalization
  const policiesPendingInstitutionalization = policies.filter((p) =>
    p.status === 'approved' && (isCommander || p.designed_by === effectiveUserEmail)
  );

  const capabilitiesPendingInstitutionalization = capabilities.filter((c) =>
    c.status === 'approved' && (isCommander || c.requested_by === effectiveUserEmail)
  );

  // Pending Bot Approvals (commander only)
  const pendingBotApprovals = isCommander && viewingOwnDashboard ? officers.filter(o => 
    o.is_bot && o.bot_approval_status === 'pending'
  ) : [];

  const totalActionItems =
  missionsAwaitingApproval.length +
  relevantPendingObjectives.length +
  activitiesWithStaleReports.length +
  activitiesWithNoReports.length +
  pendingApprovalActivities.length +
  activitiesWithReportsWaitingApproval.length +
  pendingValidationReports.length +
  unresolvedIssues.length +
  pendingTrainingRequests.length +
  overdueActivities.length +
  highSeverityUnaddressedIssues.length +
  unassignedMissions.length +
  staleArtifactsPendingApproval.length +
  staleRitualsPendingApproval.length +
  overdueArtifactAudits.length +
  overdueRitualAudits.length +
  criticalMissions.length +
  criticalObjectives.length +
  objectivesWithoutActivities.length +
  criticalActivities.length +
  overdueDailyTasks.length +
  criticalDailyTasks.length +
  tentativeTasksAllocatedByMe.length +
  tentativeTasksAllocatedToMe.length +
  tentativeTasksAwaitingMyApproval.length +
  policiesPendingReview.length +
  capabilitiesRequested.length +
  artifactsPendingApproval.length +
  ritualsPendingApproval.length +
  artifactsPendingInstitutionalization.length +
  ritualsPendingInstitutionalization.length +
  policiesPendingInstitutionalization.length +
  capabilitiesPendingInstitutionalization.length;

  const activateObjectiveMutation = useMutation({
    mutationFn: (id) => base44.entities.Objective.update(id, {
      status: 'active',
      activation_approved_by: user?.email,
      activation_approved_at: new Date().toISOString()
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingObjectives'] });
      queryClient.invalidateQueries({ queryKey: ['objectives'] });
      queryClient.invalidateQueries({ queryKey: ['allObjectives'] });
    }
  });

  const pingCommandersMutation = useMutation({
    mutationFn: async ({ missionId, objectiveStatement }) => {
      const mission = missions.find((m) => m.id === missionId);
      if (!mission) return;

      const emails = [mission.sponsor_email, mission.owner_email].filter(Boolean);

      for (const email of emails) {
        await base44.integrations.Core.SendEmail({
          to: email,
          subject: `Objective Activation Request: ${mission.title}`,
          body: `An officer has requested activation for an objective:\n\n"${objectiveStatement}"\n\nPlease review and approve in your Command Deck.`
        });
      }
    }
  });

  const dismissActionItemMutation = useMutation({
    mutationFn: ({ itemType, itemId }) => 
      base44.entities.ActionItemDismissal.create({
        user_email: effectiveUserEmail,
        item_type: itemType,
        item_id: itemId,
        action: 'dismissed'
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['actionDismissals'] })
  });

  const snoozeActionItemMutation = useMutation({
    mutationFn: ({ itemType, itemId, snoozeUntil }) => 
      base44.entities.ActionItemDismissal.create({
        user_email: effectiveUserEmail,
        item_type: itemType,
        item_id: itemId,
        action: 'snoozed',
        snooze_until: snoozeUntil.toISOString()
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['actionDismissals'] })
  });

  // Approve/Institutionalize mutations
  const approveArtifactMutation = useMutation({
    mutationFn: (id) => base44.entities.Artifact.update(id, {
      status: 'approved',
      approved_by: user?.email,
      approved_at: new Date().toISOString()
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['artifacts'] })
  });

  const institutionalizeArtifactMutation = useMutation({
    mutationFn: (id) => base44.entities.Artifact.update(id, {
      status: 'institutionalized',
      institutionalized_by: user?.email,
      institutionalized_at: new Date().toISOString()
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['artifacts'] })
  });

  const approveRitualMutation = useMutation({
    mutationFn: (id) => base44.entities.Ritual.update(id, {
      status: 'approved',
      approved_by: user?.email,
      approved_at: new Date().toISOString()
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rituals'] })
  });

  const institutionalizeRitualMutation = useMutation({
    mutationFn: (id) => base44.entities.Ritual.update(id, {
      status: 'institutionalized',
      institutionalized_by: user?.email,
      institutionalized_at: new Date().toISOString()
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rituals'] })
  });

  const approvePolicyMutation = useMutation({
    mutationFn: (id) => base44.entities.Policy.update(id, {
      status: 'approved',
      approved_by: user?.email,
      approved_at: new Date().toISOString()
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['policies'] })
  });

  const institutionalizePolicyMutation = useMutation({
    mutationFn: (id) => base44.entities.Policy.update(id, {
      status: 'institutionalized',
      institutionalized_by: user?.email,
      institutionalized_at: new Date().toISOString()
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['policies'] })
  });

  const approveCapabilityMutation = useMutation({
    mutationFn: (id) => base44.entities.Capability.update(id, {
      status: 'approved',
      approved_by: user?.email,
      approved_at: new Date().toISOString()
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['capabilities'] })
  });

  const institutionalizeCapabilityMutation = useMutation({
    mutationFn: (id) => base44.entities.Capability.update(id, {
      status: 'institutionalized',
      institutionalized_by: user?.email,
      institutionalized_at: new Date().toISOString()
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['capabilities'] })
  });

  const handleDismiss = (itemType, itemId) => {
    dismissActionItemMutation.mutate({ itemType, itemId });
  };

  const handleSnooze = (itemType, itemId, snoozeUntil) => {
    snoozeActionItemMutation.mutate({ itemType, itemId, snoozeUntil });
  };

  // Calculate metrics for cards
  const missionMetrics = {
    total: activeMissions.length,
    totalXP: activeMissions.reduce((sum, m) => sum + (m.xp_reward || 0), 0),
    byStatus: {
      green: activeMissions.filter((m) => m.status_flag === 'green').length,
      yellow: activeMissions.filter((m) => m.status_flag === 'yellow').length,
      red: activeMissions.filter((m) => m.status_flag === 'red').length
    }
  };

  const objectiveMetrics = {
    total: activeObjectives.length,
    totalXP: activeObjectives.length * 100, // Placeholder XP calculation
    byStatus: {
      green: activeObjectives.filter((o) => o.status_flag === 'green').length,
      yellow: activeObjectives.filter((o) => o.status_flag === 'yellow').length,
      red: activeObjectives.filter((o) => o.status_flag === 'red').length
    }
  };

  const activityMetrics = {
    total: activeActivities.length,
    totalXP: activeActivities.length * 50,
    totalSP: activeActivities.reduce((sum, a) => {
      return sum + (a.skill_rewards?.reduce((s, r) => s + (r.sp_amount || 0), 0) || 0);
    }, 0),
    byStatus: {
      green: activeActivities.filter((a) => a.key_results_status === 'green').length,
      yellow: activeActivities.filter((a) => a.key_results_status === 'yellow').length,
      red: activeActivities.filter((a) => a.key_results_status === 'red').length
    }
  };

  const taskMetrics = {
    total: activeTasks.length,
    totalXP: activeTasks.length * 10,
    totalSP: activeTasks.length * 5,
    byStatus: {
      green: activeTasks.filter((t) => t.status_flag === 'green').length,
      yellow: activeTasks.filter((t) => t.status_flag === 'yellow').length,
      red: activeTasks.filter((t) => t.status_flag === 'red').length
    }
  };

  // Calculate action items by officer
  const actionItemsByOfficer = React.useMemo(() => {
    const countByOfficer = {};

    // Helper to add count
    const addCount = (email) => {
      if (email) countByOfficer[email] = (countByOfficer[email] || 0) + 1;
    };

    // 1. Pending activations
    relevantPendingObjectives.forEach((obj) => addCount(obj.owner_email));

    // 2. Stale progress reports
    activitiesWithStaleReports.forEach((a) => {
      addCount(a.responsible_email);
      addCount(a.accountable_email);
    });

    // 3. No progress reports
    activitiesWithNoReports.forEach((a) => {
      addCount(a.responsible_email);
      addCount(a.accountable_email);
    });

    // Pending approval activities
    pendingApprovalActivities.forEach((a) => {
      addCount(a.accountable_email);
    });

    // Activities with reports waiting approval
    activitiesWithReportsWaitingApproval.forEach((a) => {
      addCount(a.accountable_email);
    });

    // Pending validations (commander only viewing own dashboard)
    if (isCommander && viewingOwnDashboard) {
      pendingValidationReports.forEach(({ activity }) => addCount(activity.responsible_email));
    }

    // 7. Unresolved issues
    unresolvedIssues.forEach((i) => {
      addCount(i.reported_by);
      i.related_officers?.forEach((email) => addCount(email));
    });

    // 8. Training requests
    pendingTrainingRequests.forEach((tr) => addCount(tr.officer_email));

    // 9. Overdue activities
    overdueActivities.forEach((a) => {
      addCount(a.responsible_email);
      addCount(a.accountable_email);
    });

    // 10. High-severity unaddressed issues
    highSeverityUnaddressedIssues.forEach((i) => {
      addCount(i.reported_by);
      i.related_officers?.forEach((email) => addCount(email));
    });

    // 11. Unassigned missions
    unassignedMissions.forEach((m) => {
      addCount(m.sponsor_email);
      addCount(m.owner_email);
    });

    // 12. Stale artifacts/rituals
    staleArtifactsPendingApproval.forEach((a) => addCount(a.owner_email));
    staleRitualsPendingApproval.forEach((r) => addCount(r.owner_email));

    // 13. Overdue audits
    overdueArtifactAudits.forEach((a) => addCount(a.owner_email));
    overdueRitualAudits.forEach((r) => addCount(r.owner_email));

    return Object.entries(countByOfficer).map(([email, count]) => ({ email, count }));
  }, [
  relevantPendingObjectives, activitiesWithStaleReports, activitiesWithNoReports,
  pendingApprovalActivities, activitiesWithReportsWaitingApproval, pendingValidationReports,
  unresolvedIssues, pendingTrainingRequests, overdueActivities,
  highSeverityUnaddressedIssues, unassignedMissions,
  staleArtifactsPendingApproval, staleRitualsPendingApproval,
  overdueArtifactAudits, overdueRitualAudits, isCommander, viewingOwnDashboard, activities]
  );

  // Calculate tagged items from all report issues - count once per activity
  const taggedItemsData = React.useMemo(() => {
    const tagActivitySets = {
      officers: {},
      artifacts: {},
      rituals: {},
      policies: {},
      capabilities: {}
    };

    // Track unique activities per tag
    reportIssues.forEach((reportIssue) => {
      const report = progressReports.find((r) => r.id === reportIssue.progress_report_id);
      const activityId = report?.activity_id;
      if (!activityId) return;

      const officerField = getTagField('officer');
      if (officerField) {
        (reportIssue[officerField] || []).forEach((email) => {
          if (!tagActivitySets.officers[email]) tagActivitySets.officers[email] = new Set();
          tagActivitySets.officers[email].add(activityId);
        });
      }

      const artifactField = getTagField('artifact');
      if (artifactField) {
        (reportIssue[artifactField] || []).forEach((nameOrId) => {
          if (!tagActivitySets.artifacts[nameOrId]) tagActivitySets.artifacts[nameOrId] = new Set();
          tagActivitySets.artifacts[nameOrId].add(activityId);
        });
      }

      const ritualField = getTagField('ritual');
      if (ritualField) {
        (reportIssue[ritualField] || []).forEach((nameOrId) => {
          if (!tagActivitySets.rituals[nameOrId]) tagActivitySets.rituals[nameOrId] = new Set();
          tagActivitySets.rituals[nameOrId].add(activityId);
        });
      }

      const policyField = getTagField('policy');
      if (policyField) {
        (reportIssue[policyField] || []).forEach((nameOrId) => {
          if (!tagActivitySets.policies[nameOrId]) tagActivitySets.policies[nameOrId] = new Set();
          tagActivitySets.policies[nameOrId].add(activityId);
        });
      }

      const capabilityField = getTagField('capability');
      if (capabilityField) {
        (reportIssue[capabilityField] || []).forEach((nameOrId) => {
          if (!tagActivitySets.capabilities[nameOrId]) tagActivitySets.capabilities[nameOrId] = new Set();
          tagActivitySets.capabilities[nameOrId].add(activityId);
        });
      }
    });

    // Convert to arrays and get top items
    const allItems = [];

    Object.entries(tagActivitySets.officers).forEach(([email, activitySet]) => {
      const officer = officers.find((o) => o.user_email === email);
      const name = officer?.callsign || email.split('@')[0];
      allItems.push({
        type: 'officer',
        id: email,
        name,
        originalValue: email, // What's actually stored in DB
        count: activitySet.size
      });
    });

    Object.entries(tagActivitySets.artifacts).forEach(([nameOrId, activitySet]) => {
      // Try to find by ID first, then by name
      const artifact = artifacts.find((a) => a.id === nameOrId || a.name === nameOrId);
      const name = artifact?.name || nameOrId;

      allItems.push({
        type: 'artifact',
        id: artifact?.id || nameOrId,
        name,
        originalValue: nameOrId, // What's actually stored in DB
        count: activitySet.size
      });
    });

    Object.entries(tagActivitySets.rituals).forEach(([nameOrId, activitySet]) => {
      // Try to find by ID first, then by name
      const ritual = rituals.find((r) => r.id === nameOrId || r.name === nameOrId);
      const name = ritual?.name || nameOrId;

      allItems.push({
        type: 'ritual',
        id: ritual?.id || nameOrId,
        name,
        originalValue: nameOrId, // What's actually stored in DB
        count: activitySet.size
      });
    });

    Object.entries(tagActivitySets.policies).forEach(([nameOrId, activitySet]) => {
      // Try to find by ID first, then by title
      const policy = policies.find((p) => p.id === nameOrId || p.title === nameOrId);
      const name = policy?.title || nameOrId;

      allItems.push({
        type: 'policy',
        id: policy?.id || nameOrId,
        name,
        originalValue: nameOrId, // What's actually stored in DB
        count: activitySet.size
      });
    });

    Object.entries(tagActivitySets.capabilities).forEach(([nameOrId, activitySet]) => {
      // Try to find by ID first, then by name
      const capability = capabilities.find((c) => c.id === nameOrId || c.name === nameOrId);
      const name = capability?.name || nameOrId;

      allItems.push({
        type: 'capability',
        id: capability?.id || nameOrId,
        name,
        originalValue: nameOrId, // What's actually stored in DB
        count: activitySet.size
      });
    });

    return allItems.sort((a, b) => b.count - a.count);
  }, [reportIssues, progressReports, officers, artifacts, rituals, policies, capabilities]);

  const issuesByOfficer = React.useMemo(() => {
    const countByOfficer = {};

    unresolvedIssues.forEach((issue) => {
      const emails = [issue.reported_by, ...(issue.related_officers || [])];
      emails.forEach((email) => {
        if (!email) return;
        if (!countByOfficer[email]) {
          countByOfficer[email] = { critical: 0, high: 0, medium: 0, low: 0, total: 0 };
        }
        const severity = issue.severity || 'medium';
        countByOfficer[email][severity] = (countByOfficer[email][severity] || 0) + 1;
        countByOfficer[email].total += 1;
      });
    });

    return Object.entries(countByOfficer).map(([email, counts]) => ({ email, ...counts }));
  }, [unresolvedIssues]);

  // Calculate struggling and total principles/values/red lines across all progress reports
  const principlesStats = React.useMemo(() => {
    const strugglingActivityItems = new Map(); // Track which activities have flagged which items
    const totalActivityItems = new Map(); // Track which activities have which items

    progressReports.forEach((report) => {
      const activity = activities.find((a) => a.id === report.activity_id);
      if (activity) {
        const allItems = [
        ...(activity.principles_adhered || []),
        ...(activity.red_line_considerations || []).
        filter((rl) => rl.status !== 'not_applicable').
        map((rl) => rl.red_line)];


        // Track total occurrences per activity (only count once per activity)
        allItems.forEach((item) => {
          const key = `${activity.id}_${item}`;
          if (!totalActivityItems.has(key)) {
            totalActivityItems.set(key, true);
          }
        });

        // Track struggling occurrences per activity (only count once per activity, only from approved reports)
        if (report.approved) {
          (report.flagged_items || []).forEach((item) => {
            const key = `${activity.id}_${item}`;
            if (!strugglingActivityItems.has(key)) {
              strugglingActivityItems.set(key, true);
            }
          });
        }
      }
    });

    // Convert to counts
    const strugglingCounts = {};
    const totalCounts = {};

    strugglingActivityItems.forEach((_, key) => {
      const item = key.split('_').slice(1).join('_');
      strugglingCounts[item] = (strugglingCounts[item] || 0) + 1;
    });

    totalActivityItems.forEach((_, key) => {
      const item = key.split('_').slice(1).join('_');
      totalCounts[item] = (totalCounts[item] || 0) + 1;
    });

    return { strugglingCounts, totalCounts };
  }, [progressReports, activities]);

  // Calculate Strategy/Tactics data with XP and mission flags
  const strategyTacticsData = React.useMemo(() => {
    const strategyTacticsNodes = strategicNodes.filter((n) =>
    (n.type === 'strategy' || n.type === 'tactics') && n.status === 'active'
    );

    return strategyTacticsNodes.map((node) => {
      // Find missions linked to this node
      const linkedMissions = activeMissions.filter((m) =>
      m.linked_priorities?.includes(node.id)
      );

      const xp = linkedMissions.reduce((sum, m) => sum + (m.xp_reward || 0), 0);

      const missionsByFlag = {
        green: linkedMissions.filter((m) => m.status_flag === 'green').length,
        yellow: linkedMissions.filter((m) => m.status_flag === 'yellow').length,
        red: linkedMissions.filter((m) => m.status_flag === 'red').length
      };

      return { node, xp, missionsByFlag };
    }).sort((a, b) => b.xp - a.xp);
  }, [strategicNodes, activeMissions]);

  const strugglingTopItems = React.useMemo(() => {
    const { strugglingCounts, totalCounts } = principlesStats;

    // Get items that have been reported on (have total count)
    const itemsWithData = Object.keys(totalCounts).map((item) => ({
      item,
      struggling: strugglingCounts[item] || 0,
      total: totalCounts[item] || 0
    }));

    const sorted = itemsWithData.sort((a, b) => b.total - a.total);

    const top5 = sorted.slice(0, 5);
    const others = sorted.slice(5);
    const othersStrugglingTotal = others.reduce((sum, { struggling }) => sum + struggling, 0);
    const othersTotalTotal = others.reduce((sum, { total }) => sum + total, 0);

    return {
      top5,
      othersStrugglingTotal,
      othersTotalTotal
    };
  }, [principlesStats]);



  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-4">

        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-white mb-1">
              Command Deck {!viewingOwnDashboard && <span className="text-indigo-400">- {profile?.callsign}'s View</span>}
            </h1>
            <p className="text-slate-400 flex items-center gap-2">
              <Users className="w-4 h-4 text-emerald-500" />
              {profile?.rank_title} {profile?.callsign}
              {totalActionItems > 0 &&
              <>
                  <span className="text-slate-600">•</span>
                  <span className="text-amber-400">{totalActionItems} action {totalActionItems === 1 ? 'item' : 'items'} require attention</span>
                </>
              }
            </p>
          </div>
        </div>

        {/* Officer Selector for Commanders */}
        {isCommander &&
        <div className="flex items-center gap-3 p-4 bg-slate-900/50 rounded-lg border border-slate-800">
            <Label className="text-slate-300 whitespace-nowrap">View Dashboard For:</Label>
            <Select value={viewingOfficerEmail || user?.email} onValueChange={setViewingOfficerEmail}>
              <SelectTrigger className="bg-slate-950 border-slate-700 max-w-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={user?.email}>
                  My Own Dashboard
                </SelectItem>
                {officers.filter((o) => o.user_email !== user?.email).map((officer) =>
              <SelectItem key={officer.id} value={officer.user_email}>
                    {officer.callsign} - {officer.rank_title}
                  </SelectItem>
              )}
              </SelectContent>
            </Select>
          </div>
        }
      </motion.div>

      {/* First Row - Active Work Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="glass-panel border-none overflow-hidden">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-indigo-400" />
                <CardTitle className="text-slate-700 text-lg font-semibold tracking-tight">Active Missions</CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="flex-1 relative">
                <div className="h-8 bg-slate-200 rounded-lg overflow-hidden flex">
                  {missionMetrics.total === 0 ?
                  <div className="w-full bg-slate-300" /> :

                  <>
                      {missionMetrics.byStatus.green > 0 &&
                    <div className="bg-emerald-500 flex items-center justify-center text-white font-bold text-base" style={{ width: `${missionMetrics.byStatus.green / missionMetrics.total * 100}%` }}>
                          {missionMetrics.byStatus.green}
                        </div>
                    }
                      {missionMetrics.byStatus.yellow > 0 &&
                    <div className="bg-amber-500 flex items-center justify-center text-white font-bold text-base" style={{ width: `${missionMetrics.byStatus.yellow / missionMetrics.total * 100}%` }}>
                          {missionMetrics.byStatus.yellow}
                        </div>
                    }
                      {missionMetrics.byStatus.red > 0 &&
                    <div className="bg-red-500 flex items-center justify-center text-white font-bold text-base" style={{ width: `${missionMetrics.byStatus.red / missionMetrics.total * 100}%` }}>
                          {missionMetrics.byStatus.red}
                        </div>
                    }
                    </>
                  }
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-700">
                {missionMetrics.total}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-panel border-none overflow-hidden">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-cyan-400" />
                <CardTitle className="text-slate-700 text-lg font-semibold tracking-tight">Active Objectives</CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="flex-1 relative">
                <div className="h-8 bg-slate-200 rounded-lg overflow-hidden flex">
                  {objectiveMetrics.total === 0 ?
                  <div className="w-full bg-slate-300" /> :

                  <>
                      {objectiveMetrics.byStatus.green > 0 &&
                    <div className="bg-emerald-500 flex items-center justify-center text-white font-bold text-base" style={{ width: `${objectiveMetrics.byStatus.green / objectiveMetrics.total * 100}%` }}>
                          {objectiveMetrics.byStatus.green}
                        </div>
                    }
                      {objectiveMetrics.byStatus.yellow > 0 &&
                    <div className="bg-amber-500 flex items-center justify-center text-white font-bold text-base" style={{ width: `${objectiveMetrics.byStatus.yellow / objectiveMetrics.total * 100}%` }}>
                          {objectiveMetrics.byStatus.yellow}
                        </div>
                    }
                      {objectiveMetrics.byStatus.red > 0 &&
                    <div className="bg-red-500 flex items-center justify-center text-white font-bold text-base" style={{ width: `${objectiveMetrics.byStatus.red / objectiveMetrics.total * 100}%` }}>
                          {objectiveMetrics.byStatus.red}
                        </div>
                    }
                    </>
                  }
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-700">
                {objectiveMetrics.total}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-panel border-none overflow-hidden">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-emerald-400" />
                <CardTitle className="text-slate-700 text-lg font-semibold tracking-tight">Active Activities</CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="flex-1 relative">
                <div className="h-8 bg-slate-200 rounded-lg overflow-hidden flex">
                  {activityMetrics.total === 0 ?
                  <div className="w-full bg-slate-300" /> :

                  <>
                      {activityMetrics.byStatus.green > 0 &&
                    <div className="bg-emerald-500 flex items-center justify-center text-white font-bold text-base" style={{ width: `${activityMetrics.byStatus.green / activityMetrics.total * 100}%` }}>
                          {activityMetrics.byStatus.green}
                        </div>
                    }
                      {activityMetrics.byStatus.yellow > 0 &&
                    <div className="bg-amber-500 flex items-center justify-center text-white font-bold text-base" style={{ width: `${activityMetrics.byStatus.yellow / activityMetrics.total * 100}%` }}>
                          {activityMetrics.byStatus.yellow}
                        </div>
                    }
                      {activityMetrics.byStatus.red > 0 &&
                    <div className="bg-red-500 flex items-center justify-center text-white font-bold text-base" style={{ width: `${activityMetrics.byStatus.red / activityMetrics.total * 100}%` }}>
                          {activityMetrics.byStatus.red}
                        </div>
                    }
                    </>
                  }
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-700">
                {activityMetrics.total}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-panel border-none overflow-hidden">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-amber-400" />
                <CardTitle className="text-slate-700 text-lg font-semibold tracking-tight">Active Tasks</CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="flex-1 relative">
                <div className="h-8 bg-slate-200 rounded-lg overflow-hidden flex">
                  {taskMetrics.total === 0 ?
                  <div className="w-full bg-slate-300" /> :

                  <>
                      {taskMetrics.byStatus.green > 0 &&
                    <div className="bg-emerald-500 flex items-center justify-center text-white font-bold text-base" style={{ width: `${taskMetrics.byStatus.green / taskMetrics.total * 100}%` }}>
                          {taskMetrics.byStatus.green}
                        </div>
                    }
                      {taskMetrics.byStatus.yellow > 0 &&
                    <div className="bg-amber-500 flex items-center justify-center text-white font-bold text-base" style={{ width: `${taskMetrics.byStatus.yellow / taskMetrics.total * 100}%` }}>
                          {taskMetrics.byStatus.yellow}
                        </div>
                    }
                      {taskMetrics.byStatus.red > 0 &&
                    <div className="bg-red-500 flex items-center justify-center text-white font-bold text-base" style={{ width: `${taskMetrics.byStatus.red / taskMetrics.total * 100}%` }}>
                          {taskMetrics.byStatus.red}
                        </div>
                    }
                    </>
                  }
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-700">
                {taskMetrics.total}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Items Dashboard - Moved to top */}
      <Card className="glass-panel border-none border-l-4 border-l-amber-500">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-slate-900 font-semibold tracking-tight leading-none flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-400" />
              Action Required ({totalActionItems})
            </CardTitle>
            {totalActionItems > 0 &&
            <Badge className="bg-amber-500/20 text-amber-400 border-none">
                Immediate Attention
              </Badge>
            }
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
            
            {/* I. Mission Command */}
            <Collapsible open={expandedCategories.missionCommand} onOpenChange={() => toggleCategory('missionCommand')}>
              <CollapsibleTrigger className="w-full mb-3 pl-1 flex items-center justify-between hover:opacity-80 transition-opacity">
                <h3 className="text-amber-500 text-base font-extrabold uppercase tracking-wider">
                  I. MISSION COMMAND ({missionsAwaitingApproval.length + unassignedMissions.length + criticalMissions.length})
                </h3>
                <ChevronRight className={`w-5 h-5 text-amber-500 transition-transform ${expandedCategories.missionCommand ? 'rotate-90' : ''}`} />
              </CollapsibleTrigger>
              <CollapsibleContent>
              <div className="space-y-3">
                {/* Missions Awaiting Approval */}
                <Collapsible>
                  <div className={`rounded-lg border ${missionsAwaitingApproval.length > 0 ? 'bg-yellow-50 border-indigo-500/30' : 'bg-white border-slate-200'}`}>
                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <Target className="w-5 h-5 text-indigo-400" />
                        <div className="text-left">
                          <h3 className="text-slate-900 font-semibold">Missions Awaiting Approval</h3>
                          <p className="text-xs text-slate-500">Missions in briefing status pending activation</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={missionsAwaitingApproval.length > 0 ? "bg-indigo-500/20 text-indigo-400 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                          {missionsAwaitingApproval.length}
                        </Badge>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform`} />
                      </div>
                    </CollapsibleTrigger>
                    {missionsAwaitingApproval.length > 0 && (
                      <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                          {missionsAwaitingApproval.map((mission) => (
                            <div key={mission.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <Link to={createPageUrl('missions')}>
                                <div className="text-sm font-medium text-slate-900 mb-1">[{mission.mission_code}] {mission.title}</div>
                                <div className="text-xs text-slate-500 mb-2">
                                  Sponsor: {getOfficerDisplay(mission.sponsor_email)} • Owner: {getOfficerDisplay(mission.owner_email)}
                                </div>
                              </Link>
                              <ActionItemButtons
                                itemType="mission_awaiting_approval"
                                itemId={mission.id}
                                onDismiss={handleDismiss}
                                onSnooze={handleSnooze}
                              />
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    )}
                  </div>
                </Collapsible>

                {/* Unassigned Missions */}
                <Collapsible>
                  <div className={`rounded-lg border ${unassignedMissions.length > 0 ? 'bg-yellow-50 border-cyan-500/30' : 'bg-white border-slate-200'}`}>
                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <Users className="w-5 h-5 text-cyan-400" />
                        <div className="text-left">
                          <h3 className="text-slate-900 font-semibold">Unassigned Missions</h3>
                          <p className="text-xs text-slate-500">Missions missing Sponsor or Owner</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={unassignedMissions.length > 0 ? "bg-cyan-500/20 text-cyan-400 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                          {unassignedMissions.length}
                        </Badge>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform`} />
                      </div>
                    </CollapsibleTrigger>
                    {unassignedMissions.length > 0 && (
                      <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                          {unassignedMissions.map((mission) => (
                            <div key={mission.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <Link to={createPageUrl('missions')}>
                                <div className="text-sm font-medium text-slate-900 mb-1">[{mission.mission_code}] {mission.title}</div>
                                <div className="text-xs text-slate-500 mb-2">Sponsor: {getOfficerDisplay(mission.sponsor_email)} • Owner: {getOfficerDisplay(mission.owner_email)}</div>
                              </Link>
                              <ActionItemButtons
                                itemType="unassigned_mission"
                                itemId={mission.id}
                                onDismiss={handleDismiss}
                                onSnooze={handleSnooze}
                              />
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    )}
                  </div>
                </Collapsible>

                {/* Critical Missions */}
                <Collapsible open={expandedSections.criticalMissions} onOpenChange={() => toggleSection('criticalMissions')}>
                  <div className={`rounded-lg border ${criticalMissions.length > 0 ? 'bg-red-50 border-red-500/30' : 'bg-white border-slate-200'}`}>
                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <Target className="w-5 h-5 text-red-500" />
                        <div className="text-left">
                          <h3 className="text-slate-900 font-semibold">Missions with Critical Status</h3>
                          <p className="text-xs text-slate-500">Active missions flagged as off track</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={criticalMissions.length > 0 ? "bg-red-500/20 text-red-500 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                          {criticalMissions.length}
                        </Badge>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedSections.criticalMissions ? 'rotate-90' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                    {criticalMissions.length > 0 && (
                      <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                          {criticalMissions.map((mission) => (
                            <div key={mission.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <Link to={createPageUrl('missions')}>
                                <div className="flex items-center justify-between mb-2">
                                  <div>
                                    <div className="text-sm font-medium text-slate-900">[{mission.mission_code}] {mission.title}</div>
                                    <div className="text-xs text-slate-500">Owned by {getOfficerDisplay(mission.owner_email)}</div>
                                  </div>
                                  <Badge variant="outline" className="border-red-500 text-red-600">Critical</Badge>
                                </div>
                              </Link>
                              <ActionItemButtons
                                itemType="critical_mission"
                                itemId={mission.id}
                                onDismiss={handleDismiss}
                                onSnooze={handleSnooze}
                              />
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    )}
                  </div>
                </Collapsible>
              </div>
              </CollapsibleContent>
            </Collapsible>

            {/* II. Objective Deployment */}
            <Collapsible open={expandedCategories.objectiveDeployment} onOpenChange={() => toggleCategory('objectiveDeployment')}>
              <CollapsibleTrigger className="w-full mb-3 pl-1 flex items-center justify-between hover:opacity-80 transition-opacity">
                <h3 className="text-amber-500 text-base font-extrabold uppercase tracking-wider">
                  II. OBJECTIVE DEPLOYMENT ({relevantPendingObjectives.length + criticalObjectives.length + objectivesWithoutActivities.length})
                </h3>
                <ChevronRight className={`w-5 h-5 text-amber-500 transition-transform ${expandedCategories.objectiveDeployment ? 'rotate-90' : ''}`} />
              </CollapsibleTrigger>
              <CollapsibleContent>
              <div className="space-y-3">
                {/* Objectives Pending Activation */}
                <Collapsible open={expandedSections.activations} onOpenChange={() => toggleSection('activations')}>
                  <div className={`rounded-lg border ${relevantPendingObjectives.length > 0 ? 'bg-yellow-50 border-amber-500/30' : 'bg-white border-slate-200'}`}>
                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <Target className="w-5 h-5 text-amber-400" />
                        <div className="text-left">
                          <h3 className="text-slate-900 font-semibold">Objectives Pending Activation</h3>
                          <p className="text-xs text-slate-500">Review and approve objectives</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={relevantPendingObjectives.length > 0 ? "bg-amber-500/20 text-amber-400 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                          {relevantPendingObjectives.length}
                        </Badge>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedSections.activations ? 'rotate-90' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                    {relevantPendingObjectives.length > 0 &&
                  <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-3">
                          {relevantPendingObjectives.map((objective) => {
                        const mission = missions.find((m) => m.id === objective.mission_id);
                        const isRequester = objective.activation_requested_by === user?.email;
                        const canApprove = mission && (mission.sponsor_email === user?.email || mission.owner_email === user?.email) && isCommander;

                        return (
                          <div key={objective.id} className="p-3 bg-white rounded-lg border border-slate-200 hover:shadow-md transition-shadow">
                                <div className="mb-2">
                                  <h4 className="text-slate-900 font-medium text-sm">[{objective.objective_code}] {objective.statement}</h4>
                                  {mission && <p className="text-xs text-slate-500 mt-1">Mission: [{mission.mission_code}] {mission.title}</p>}
                                </div>
                                <div className="text-xs text-slate-600 mb-2 flex items-center gap-2">
                                  <Clock className="w-3 h-3" />
                                  Requested by {getOfficerDisplay(objective.activation_requested_by)}
                                </div>
                                <div className="flex gap-2 items-center justify-between">
                                  <div className="flex gap-2">
                                    {canApprove &&
                                <Button data-testid="dash-approve-btn" size="sm" onClick={() => activateObjectiveMutation.mutate(objective.id)} className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs">
                                        <CheckCircle className="w-3 h-3 mr-2" /> Approve
                                      </Button>
                                }
                                    {isRequester && !canApprove &&
                                <Button data-testid="dash-ping-btn" size="sm" variant="outline" onClick={() => pingCommandersMutation.mutate({ missionId: mission?.id, objectiveStatement: objective.statement })} className="border-amber-500/30 text-amber-400 h-8 text-xs">
                                        <Bell className="w-3 h-3 mr-2" /> Ping
                                      </Button>
                                }
                                    <Link to={createPageUrl(`discovery?mission=${mission?.id}`)}>
                                      <Button size="sm" variant="ghost" className="h-8 text-xs">Details</Button>
                                    </Link>
                                  </div>
                                  <ActionItemButtons
                                    itemType="pending_objective"
                                    itemId={objective.id}
                                    onDismiss={handleDismiss}
                                    onSnooze={handleSnooze}
                                  />
                                </div>
                              </div>);

                      })}
                        </div>
                      </CollapsibleContent>
                  }
                  </div>
                </Collapsible>

                {/* Critical Objectives */}
                <Collapsible open={expandedSections.criticalObjectives} onOpenChange={() => toggleSection('criticalObjectives')}>
                  <div className={`rounded-lg border ${criticalObjectives.length > 0 ? 'bg-red-50 border-red-500/30' : 'bg-white border-slate-200'}`}>
                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                        <div className="text-left">
                          <h3 className="text-slate-900 font-semibold">Objectives with Critical Status</h3>
                          <p className="text-xs text-slate-500">Active objectives flagged as off track</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={criticalObjectives.length > 0 ? "bg-red-500/20 text-red-500 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                          {criticalObjectives.length}
                        </Badge>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedSections.criticalObjectives ? 'rotate-90' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                    {criticalObjectives.length > 0 && (
                      <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                          {criticalObjectives.map((obj) => (
                            <div key={obj.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <Link to={createPageUrl('discovery')}>
                                <div className="text-sm font-medium text-slate-900 mb-1">[{obj.objective_code}] {obj.statement}</div>
                                <div className="text-xs text-slate-500 mb-2">Owner: {getOfficerDisplay(obj.owner_email)} • Status: Red</div>
                              </Link>
                              <ActionItemButtons
                                itemType="critical_objective"
                                itemId={obj.id}
                                onDismiss={handleDismiss}
                                onSnooze={handleSnooze}
                              />
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    )}
                  </div>
                </Collapsible>

                {/* Objectives Without Activities */}
                <Collapsible open={expandedSections.emptyObjectives} onOpenChange={() => toggleSection('emptyObjectives')}>
                  <div className={`rounded-lg border ${objectivesWithoutActivities.length > 0 ? 'bg-yellow-50 border-orange-500/30' : 'bg-white border-slate-200'}`}>
                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <FileWarning className="w-5 h-5 text-orange-400" />
                        <div className="text-left">
                          <h3 className="text-slate-900 font-semibold">Objectives Without Activities</h3>
                          <p className="text-xs text-slate-500">Active objectives with no plan of action</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={objectivesWithoutActivities.length > 0 ? "bg-orange-500/20 text-orange-400 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                          {objectivesWithoutActivities.length}
                        </Badge>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedSections.emptyObjectives ? 'rotate-90' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                    {objectivesWithoutActivities.length > 0 && (
                      <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                          {objectivesWithoutActivities.map((obj) => (
                            <div key={obj.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <Link to={createPageUrl('activities')}>
                                <div className="text-sm font-medium text-slate-900 mb-1">[{obj.objective_code}] {obj.statement}</div>
                                <div className="text-xs text-slate-500 mb-2">Accountable: {getOfficerDisplay(obj.owner_email)}</div>
                              </Link>
                              <ActionItemButtons
                                itemType="empty_objective"
                                itemId={obj.id}
                                onDismiss={handleDismiss}
                                onSnooze={handleSnooze}
                              />
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    )}
                  </div>
                </Collapsible>
              </div>
              </CollapsibleContent>
            </Collapsible>

            {/* III. Activity Operations */}
            <Collapsible open={expandedCategories.activityOperations} onOpenChange={() => toggleCategory('activityOperations')}>
              <CollapsibleTrigger className="w-full mb-3 pl-1 flex items-center justify-between hover:opacity-80 transition-opacity">
                <h3 className="text-amber-500 text-base font-extrabold uppercase tracking-wider">
                  III. ACTIVITY OPERATIONS ({overdueActivities.length + activitiesWithStaleReports.length + activitiesWithNoReports.length + pendingApprovalActivities.length + activitiesWithReportsWaitingApproval.length + pendingValidationReports.length + criticalActivities.length})
                </h3>
                <ChevronRight className={`w-5 h-5 text-amber-500 transition-transform ${expandedCategories.activityOperations ? 'rotate-90' : ''}`} />
              </CollapsibleTrigger>
              <CollapsibleContent>
              <div className="space-y-3">
                {/* Overdue Activities */}
                <Collapsible>
                  <div className={`rounded-lg border ${overdueActivities.length > 0 ? 'bg-red-50 border-red-500/30' : 'bg-white border-slate-200'}`}>
                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-red-500" />
                        <div className="text-left">
                          <h3 className="text-slate-900 font-semibold">Overdue Activities</h3>
                          <p className="text-xs text-slate-500">Activities past due date</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={overdueActivities.length > 0 ? "bg-red-500/20 text-red-500 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                          {overdueActivities.length}
                        </Badge>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform`} />
                      </div>
                    </CollapsibleTrigger>
                    {overdueActivities.length > 0 && (
                      <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                          {overdueActivities.map((activity) => (
                            <div key={activity.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <Link to={createPageUrl('activities')}>
                                <div className="text-sm font-medium text-slate-900 mb-1">[{activity.activity_code}] {activity.title}</div>
                                <div className="text-xs text-slate-500 mb-2">
                                  Resp: {getOfficerDisplay(activity.responsible_email)} • Due: {format(new Date(activity.due_date), 'MMM d')}
                                </div>
                              </Link>
                              <ActionItemButtons
                                itemType="overdue_activity"
                                itemId={activity.id}
                                onDismiss={handleDismiss}
                                onSnooze={handleSnooze}
                              />
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    )}
                  </div>
                </Collapsible>

                {/* Stale Progress Reports */}
                <Collapsible>
                  <div className={`rounded-lg border ${activitiesWithStaleReports.length > 0 ? 'bg-yellow-50 border-orange-500/30' : 'bg-white border-slate-200'}`}>
                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-orange-400" />
                        <div className="text-left">
                          <h3 className="text-slate-900 font-semibold">Activities with Stale Reports</h3>
                          <p className="text-xs text-slate-500">No updates in 5+ days</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={activitiesWithStaleReports.length > 0 ? "bg-orange-500/20 text-orange-400 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                          {activitiesWithStaleReports.length}
                        </Badge>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform`} />
                      </div>
                    </CollapsibleTrigger>
                    {activitiesWithStaleReports.length > 0 && (
                      <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                          {activitiesWithStaleReports.map((activity) => (
                            <div key={activity.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <Link to={createPageUrl('activities')}>
                                <div className="text-sm font-medium text-slate-900 mb-1">[{activity.activity_code}] {activity.title}</div>
                                <div className="text-xs text-slate-500 mb-2">Resp: {getOfficerDisplay(activity.responsible_email)}</div>
                              </Link>
                              <ActionItemButtons
                                itemType="stale_report"
                                itemId={activity.id}
                                onDismiss={handleDismiss}
                                onSnooze={handleSnooze}
                              />
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    )}
                  </div>
                </Collapsible>

                {/* No Progress Reports */}
                <Collapsible open={expandedSections.noReports} onOpenChange={() => toggleSection('noReports')}>
                  <div className={`rounded-lg border ${activitiesWithNoReports.length > 0 ? 'bg-yellow-50 border-red-500/30' : 'bg-white border-slate-200'}`}>
                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <AlertCircle className="w-5 h-5 text-red-400" />
                        <div className="text-left">
                          <h3 className="text-slate-900 font-semibold">Activities with No Reports</h3>
                          <p className="text-xs text-slate-500">Never reported on</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={activitiesWithNoReports.length > 0 ? "bg-red-500/20 text-red-400 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                          {activitiesWithNoReports.length}
                        </Badge>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedSections.noReports ? 'rotate-90' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                    {activitiesWithNoReports.length > 0 && (
                      <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                          {activitiesWithNoReports.slice(0, 5).map((activity) => (
                            <div key={activity.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <Link to={createPageUrl('activities')}>
                                <div className="text-sm font-medium text-slate-900 mb-1">[{activity.activity_code}] {activity.title}</div>
                                <div className="text-xs text-slate-500 mb-2">Resp: {getOfficerDisplay(activity.responsible_email)}</div>
                              </Link>
                              <ActionItemButtons
                                itemType="no_report"
                                itemId={activity.id}
                                onDismiss={handleDismiss}
                                onSnooze={handleSnooze}
                              />
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    )}
                  </div>
                </Collapsible>

                {/* Pending Approval */}
                <Collapsible open={expandedSections.approvals} onOpenChange={() => toggleSection('approvals')}>
                  <div className={`rounded-lg border ${pendingApprovalActivities.length > 0 ? 'bg-yellow-50 border-yellow-500/30' : 'bg-white border-slate-200'}`}>
                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <FileWarning className="w-5 h-5 text-yellow-400" />
                        <div className="text-left">
                          <h3 className="text-slate-900 font-semibold">Activities Pending Approval</h3>
                          <p className="text-xs text-slate-500">Review completed activities</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={pendingApprovalActivities.length > 0 ? "bg-yellow-500/20 text-yellow-400 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                          {pendingApprovalActivities.length}
                        </Badge>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedSections.approvals ? 'rotate-90' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                    {pendingApprovalActivities.length > 0 && (
                      <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                          {pendingApprovalActivities.map((activity) => (
                            <div key={activity.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <Link to={createPageUrl('activities')}>
                                <div className="text-sm font-medium text-slate-900 mb-1">[{activity.activity_code}] {activity.title}</div>
                                <div className="text-xs text-slate-500 mb-2">Accountable: {getOfficerDisplay(activity.accountable_email)}</div>
                              </Link>
                              <ActionItemButtons
                                itemType="pending_approval"
                                itemId={activity.id}
                                onDismiss={handleDismiss}
                                onSnooze={handleSnooze}
                              />
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    )}
                  </div>
                </Collapsible>

                {/* Progress Reports Waiting for Approval */}
                <Collapsible>
                  <div className={`rounded-lg border ${activitiesWithReportsWaitingApproval.length > 0 ? 'bg-blue-50 border-blue-500/30' : 'bg-white border-slate-200'}`}>
                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-blue-500" />
                        <div className="text-left">
                          <h3 className="text-slate-900 font-semibold">Activities with Progress Report Waiting for Approval</h3>
                          <p className="text-xs text-slate-500">Review and approve progress reports</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={activitiesWithReportsWaitingApproval.length > 0 ? "bg-blue-500/20 text-blue-500 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                          {activitiesWithReportsWaitingApproval.length}
                        </Badge>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform`} />
                      </div>
                    </CollapsibleTrigger>
                    {activitiesWithReportsWaitingApproval.length > 0 && (
                      <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                          {activitiesWithReportsWaitingApproval.map((activity) => {
                            const unapprovedReports = progressReports.filter((r) => r.activity_id === activity.id && !r.approved);
                            return (
                              <div key={activity.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                                <Link to={createPageUrl('activities')}>
                                  <div className="text-sm font-medium text-slate-900 mb-1">[{activity.activity_code}] {activity.title}</div>
                                  <div className="text-xs text-slate-500 mb-2">
                                    {unapprovedReports.length} {unapprovedReports.length === 1 ? 'report' : 'reports'} waiting • Resp: {getOfficerDisplay(activity.responsible_email)}
                                  </div>
                                </Link>
                                <ActionItemButtons
                                  itemType="report_waiting_approval"
                                  itemId={activity.id}
                                  onDismiss={handleDismiss}
                                  onSnooze={handleSnooze}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </CollapsibleContent>
                    )}
                  </div>
                </Collapsible>

                {/* Critical Activities */}
                <Collapsible open={expandedSections.criticalActivities} onOpenChange={() => toggleSection('criticalActivities')}>
                  <div className={`rounded-lg border ${criticalActivities.length > 0 ? 'bg-red-50 border-red-500/30' : 'bg-white border-slate-200'}`}>
                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                        <div className="text-left">
                          <h3 className="text-slate-900 font-semibold">Activities with Critical Status</h3>
                          <p className="text-xs text-slate-500">Key results marked as red</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={criticalActivities.length > 0 ? "bg-red-500/20 text-red-500 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                          {criticalActivities.length}
                        </Badge>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedSections.criticalActivities ? 'rotate-90' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                    {criticalActivities.length > 0 && (
                      <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                          {criticalActivities.map((activity) => (
                            <div key={activity.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <Link to={createPageUrl('activities')}>
                                <div className="text-sm font-medium text-slate-900 mb-1">[{activity.activity_code}] {activity.title}</div>
                                <div className="text-xs text-slate-500 mb-2">Resp: {getOfficerDisplay(activity.responsible_email)} • Status: Red</div>
                              </Link>
                              <ActionItemButtons
                                itemType="critical_activity"
                                itemId={activity.id}
                                onDismiss={handleDismiss}
                                onSnooze={handleSnooze}
                              />
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    )}
                  </div>
                </Collapsible>

                {/* Validations */}
                {isCommander && viewingOwnDashboard &&
              <Collapsible open={expandedSections.validations} onOpenChange={() => toggleSection('validations')}>
                    <div className={`rounded-lg border ${pendingValidationReports.length > 0 ? 'bg-indigo-50 border-indigo-500/30' : 'bg-white border-slate-200'}`}>
                      <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                        <div className="flex items-center gap-3">
                          <Shield className="w-5 h-5 text-indigo-400" />
                          <div className="text-left">
                            <h3 className="text-slate-900 font-semibold">Principles Validations Required</h3>
                            <p className="text-xs text-slate-500">Review officer reports</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={pendingValidationReports.length > 0 ? "bg-indigo-500/20 text-indigo-400 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                            {pendingValidationReports.length}
                          </Badge>
                          <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedSections.validations ? 'rotate-90' : ''}`} />
                        </div>
                      </CollapsibleTrigger>
                      {pendingValidationReports.length > 0 && (
                        <CollapsibleContent>
                          <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                            {pendingValidationReports.slice(0, 5).map(({ activity, report }) => (
                              <div key={report.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow flex items-center justify-between">
                                <div>
                                  <div className="text-sm font-medium text-slate-900 truncate flex-1 mr-2">[{activity.activity_code}] {activity.title}</div>
                                  <div className="text-xs text-slate-500">Report by {getOfficerDisplay(report.reported_by)}</div>
                                </div>
                                <Button size="sm" className="h-7 text-xs" onClick={() => setValidatingReport({ activity, report })}>Review</Button>
                              </div>
                            ))}
                          </div>
                        </CollapsibleContent>
                      )}
                    </div>
                  </Collapsible>
                  }
                  </div>
                  </CollapsibleContent>
                  </Collapsible>

                  {/* IV. Daily Task Directives */}
                  <Collapsible open={expandedCategories.dailyTaskDirectives} onOpenChange={() => toggleCategory('dailyTaskDirectives')}>
                  <CollapsibleTrigger className="w-full mb-3 pl-1 flex items-center justify-between hover:opacity-80 transition-opacity">
                  <h3 className="text-amber-500 text-base font-extrabold uppercase tracking-wider">
                  IV. DAILY TASK DIRECTIVES ({overdueDailyTasks.length + criticalDailyTasks.length + tentativeTasksAllocatedByMe.length + tentativeTasksAllocatedToMe.length + tentativeTasksAwaitingMyApproval.length})
                  </h3>
                  <ChevronRight className={`w-5 h-5 text-amber-500 transition-transform ${expandedCategories.dailyTaskDirectives ? 'rotate-90' : ''}`} />
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                  <div className="space-y-3">
                {/* Overdue Tasks */}
                <Collapsible open={expandedSections.overdueTasks} onOpenChange={() => toggleSection('overdueTasks')}>
                  <div className={`rounded-lg border ${overdueDailyTasks.length > 0 ? 'bg-red-50 border-red-500/30' : 'bg-white border-slate-200'}`}>
                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <Calendar className="w-5 h-5 text-red-500" />
                        <div className="text-left">
                          <h3 className="text-slate-900 font-semibold">Overdue Daily Tasks</h3>
                          <p className="text-xs text-slate-500">Tasks not completed on time</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={overdueDailyTasks.length > 0 ? "bg-red-500/20 text-red-500 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                          {overdueDailyTasks.length}
                        </Badge>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedSections.overdueTasks ? 'rotate-90' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                    {overdueDailyTasks.length > 0 && (
                      <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                          {overdueDailyTasks.map((task) => (
                            <div key={task.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <Link to={createPageUrl('dailytasks')}>
                                <div className="text-sm font-medium text-slate-900 mb-1">{task.task_description}</div>
                                <div className="text-xs text-slate-500 mb-2">{getOfficerDisplay(task.user_email)} • {format(new Date(task.date), 'MMM d')}</div>
                              </Link>
                              <ActionItemButtons
                                itemType="overdue_task"
                                itemId={task.id}
                                onDismiss={handleDismiss}
                                onSnooze={handleSnooze}
                              />
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    )}
                  </div>
                </Collapsible>

                {/* Critical Tasks */}
                <Collapsible open={expandedSections.criticalTasks} onOpenChange={() => toggleSection('criticalTasks')}>
                  <div className={`rounded-lg border ${criticalDailyTasks.length > 0 ? 'bg-red-50 border-red-500/30' : 'bg-white border-slate-200'}`}>
                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <Flame className="w-5 h-5 text-red-500" />
                        <div className="text-left">
                          <h3 className="text-slate-900 font-semibold">Tasks with Critical Status</h3>
                          <p className="text-xs text-slate-500">Tasks flagged as struggling</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={criticalDailyTasks.length > 0 ? "bg-red-500/20 text-red-500 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                          {criticalDailyTasks.length}
                        </Badge>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedSections.criticalTasks ? 'rotate-90' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                    {criticalDailyTasks.length > 0 && (
                      <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                          {criticalDailyTasks.map((task) => (
                            <div key={task.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <Link to={createPageUrl('dailytasks')}>
                                <div className="text-sm font-medium text-slate-900 mb-1">{task.task_description}</div>
                                <div className="text-xs text-slate-500 mb-2">{getOfficerDisplay(task.user_email)} • {format(new Date(task.date), 'MMM d')}</div>
                              </Link>
                              <ActionItemButtons
                                itemType="critical_task"
                                itemId={task.id}
                                onDismiss={handleDismiss}
                                onSnooze={handleSnooze}
                              />
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    )}
                  </div>
                </Collapsible>

                {/* Tentative Tasks Allocated By Me */}
                <Collapsible open={expandedSections.tentativeAllocatedByMe} onOpenChange={() => toggleSection('tentativeAllocatedByMe')}>
                  <div className={`rounded-lg border ${tentativeTasksAllocatedByMe.length > 0 ? 'bg-blue-50 border-blue-500/30' : 'bg-white border-slate-200'}`}>
                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <Users className="w-5 h-5 text-blue-500" />
                        <div className="text-left">
                          <h3 className="text-slate-900 font-semibold">Tentative Tasks You Allocated to Others</h3>
                          <p className="text-xs text-slate-500">Awaiting acceptance by other officers</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={tentativeTasksAllocatedByMe.length > 0 ? "bg-blue-500/20 text-blue-500 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                          {tentativeTasksAllocatedByMe.length}
                        </Badge>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedSections.tentativeAllocatedByMe ? 'rotate-90' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                    {tentativeTasksAllocatedByMe.length > 0 && (
                      <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                          {tentativeTasksAllocatedByMe.map((task) => (
                            <div key={task.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <Link to={createPageUrl('dailytasks')}>
                                <div className="text-sm font-medium text-slate-900 mb-1">{task.task_description}</div>
                                <div className="text-xs text-slate-500 mb-2">Allocated to {getOfficerDisplay(task.user_email)} • {format(new Date(task.date), 'MMM d')}</div>
                              </Link>
                              <ActionItemButtons
                                itemType="tentative_by_me"
                                itemId={task.id}
                                onDismiss={handleDismiss}
                                onSnooze={handleSnooze}
                              />
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    )}
                  </div>
                </Collapsible>

                {/* Tentative Tasks Allocated To Me */}
                <Collapsible open={expandedSections.tentativeAllocatedToMe} onOpenChange={() => toggleSection('tentativeAllocatedToMe')}>
                  <div className={`rounded-lg border ${tentativeTasksAllocatedToMe.length > 0 ? 'bg-purple-50 border-purple-500/30' : 'bg-white border-slate-200'}`}>
                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <Calendar className="w-5 h-5 text-purple-500" />
                        <div className="text-left">
                          <h3 className="text-slate-900 font-semibold">Tentative Tasks Allocated to You</h3>
                          <p className="text-xs text-slate-500">Awaiting your acceptance</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={tentativeTasksAllocatedToMe.length > 0 ? "bg-purple-500/20 text-purple-500 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                          {tentativeTasksAllocatedToMe.length}
                        </Badge>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedSections.tentativeAllocatedToMe ? 'rotate-90' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                    {tentativeTasksAllocatedToMe.length > 0 && (
                      <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                          {tentativeTasksAllocatedToMe.map((task) => (
                            <div key={task.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <Link to={createPageUrl('dailytasks')}>
                                <div className="text-sm font-medium text-slate-900 mb-1">{task.task_description}</div>
                                <div className="text-xs text-slate-500 mb-2">From {getOfficerDisplay(task.created_by_email)} • {format(new Date(task.date), 'MMM d')}</div>
                              </Link>
                              <ActionItemButtons
                                itemType="tentative_to_me"
                                itemId={task.id}
                                onDismiss={handleDismiss}
                                onSnooze={handleSnooze}
                              />
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    )}
                  </div>
                </Collapsible>

                {/* Tentative Tasks Awaiting My Approval */}
                <Collapsible open={expandedSections.tentativeAwaitingApproval} onOpenChange={() => toggleSection('tentativeAwaitingApproval')}>
                  <div className={`rounded-lg border ${tentativeTasksAwaitingMyApproval.length > 0 ? 'bg-amber-50 border-amber-500/30' : 'bg-white border-slate-200'}`}>
                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-amber-500" />
                        <div className="text-left">
                          <h3 className="text-slate-900 font-semibold">Tentative Tasks Awaiting Your Approval</h3>
                          <p className="text-xs text-slate-500">Officers requesting tasks on your activities</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={tentativeTasksAwaitingMyApproval.length > 0 ? "bg-amber-500/20 text-amber-500 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                          {tentativeTasksAwaitingMyApproval.length}
                        </Badge>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedSections.tentativeAwaitingApproval ? 'rotate-90' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                    {tentativeTasksAwaitingMyApproval.length > 0 && (
                      <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                          {tentativeTasksAwaitingMyApproval.map((task) => {
                            const activity = activities.find(a => a.id === task.activity_id);
                            return (
                              <div key={task.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                                <Link to={createPageUrl('dailytasks')}>
                                  <div className="text-sm font-medium text-slate-900 mb-1">{task.task_description}</div>
                                  <div className="text-xs text-slate-500 mb-2">
                                    By {getOfficerDisplay(task.created_by_email)} • {format(new Date(task.date), 'MMM d')}
                                    {activity && <span className="ml-2">• Activity: [{activity.activity_code}]</span>}
                                  </div>
                                </Link>
                                <ActionItemButtons
                                  itemType="tentative_awaiting_approval"
                                  itemId={task.id}
                                  onDismiss={handleDismiss}
                                  onSnooze={handleSnooze}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </CollapsibleContent>
                    )}
                  </div>
                </Collapsible>
              </div>
              </CollapsibleContent>
            </Collapsible>

            {/* V. System & Governance */}
            <Collapsible open={expandedCategories.systemGovernance} onOpenChange={() => toggleCategory('systemGovernance')}>
              <CollapsibleTrigger className="w-full mb-3 pl-1 flex items-center justify-between hover:opacity-80 transition-opacity">
                <h3 className="text-amber-500 text-base font-extrabold uppercase tracking-wider">
                  V. SYSTEM & GOVERNANCE ({unresolvedIssues.length + policiesPendingReview.length + capabilitiesRequested.length + staleArtifactsPendingApproval.length + staleRitualsPendingApproval.length + pendingTrainingRequests.length + pendingBotApprovals.length + artifactsPendingApproval.length + ritualsPendingApproval.length + artifactsPendingInstitutionalization.length + ritualsPendingInstitutionalization.length + policiesPendingInstitutionalization.length + capabilitiesPendingInstitutionalization.length + bridgingPoliciesOverdue.length})
                </h3>
                <ChevronRight className={`w-5 h-5 text-amber-500 transition-transform ${expandedCategories.systemGovernance ? 'rotate-90' : ''}`} />
              </CollapsibleTrigger>
              <CollapsibleContent>
              <div className="space-y-3">
                {/* Bridging Policies Overdue for Review */}
                {bridgingPoliciesOverdue.length > 0 && (
                  <Collapsible>
                    <div className="rounded-lg border bg-red-50 border-red-500/30">
                      <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                        <div className="flex items-center gap-3">
                          <AlertTriangle className="w-5 h-5 text-red-500" />
                          <div className="text-left">
                            <h3 className="text-slate-900 font-semibold">Bridging Policies Overdue for Review</h3>
                            <p className="text-xs text-slate-500">Interim policies past their mandatory review date</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-red-500/20 text-red-500 border-none">
                            {bridgingPoliciesOverdue.length}
                          </Badge>
                          <ChevronRight className="w-5 h-5 text-slate-400 transition-transform" />
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                          {bridgingPoliciesOverdue.map((p) => (
                            <div key={p.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <Link to={createPageUrl('issues')}>
                                <div className="text-sm font-medium text-slate-900 mb-1">{p.title}</div>
                                <div className="text-xs text-red-500 mb-1">
                                  Review was due {format(new Date(p.review_date), 'MMM d, yyyy')}
                                </div>
                                <div className="text-xs text-slate-500">Designed by {getOfficerDisplay(p.designed_by)}</div>
                              </Link>
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                )}

                {/* Pending Bot Approvals - Commanders Only */}
                {isCommander && viewingOwnDashboard && pendingBotApprovals.length > 0 && (
                  <PendingBotApprovalsCard officers={officers} currentUser={user} />
                )}

                {/* New Artifacts/Rituals Pending Approval */}
                <Collapsible>
                  <div className={`rounded-lg border ${artifactsPendingApproval.length + ritualsPendingApproval.length > 0 ? 'bg-purple-50 border-purple-500/30' : 'bg-white border-slate-200'}`}>
                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-purple-500" />
                        <div className="text-left">
                          <h3 className="text-slate-900 font-semibold">New Artifacts/Rituals Pending Approval</h3>
                          <p className="text-xs text-slate-500">Draft items awaiting commander approval</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={artifactsPendingApproval.length + ritualsPendingApproval.length > 0 ? "bg-purple-500/20 text-purple-500 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                          {artifactsPendingApproval.length + ritualsPendingApproval.length}
                        </Badge>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform`} />
                      </div>
                    </CollapsibleTrigger>
                    {(artifactsPendingApproval.length + ritualsPendingApproval.length > 0) && (
                      <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                          {artifactsPendingApproval.map((a) => (
                            <div key={a.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <div className="flex items-center justify-between">
                                <div className="flex-1 mr-2">
                                  <div className="text-sm font-medium text-slate-900 mb-1">Artifact: {a.name}</div>
                                  <div className="text-xs text-slate-500">Owner: {getOfficerDisplay(a.owner_email)}</div>
                                </div>
                                {isCommander && (
                                  <Button 
                                    size="sm" 
                                    className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs"
                                    onClick={() => approveArtifactMutation.mutate(a.id)}
                                    disabled={approveArtifactMutation.isPending}
                                  >
                                    <CheckCircle className="w-3 h-3 mr-1" /> Approve
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                          {ritualsPendingApproval.map((r) => (
                            <div key={r.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <div className="flex items-center justify-between">
                                <div className="flex-1 mr-2">
                                  <div className="text-sm font-medium text-slate-900 mb-1">Ritual: {r.name}</div>
                                  <div className="text-xs text-slate-500">Owner: {getOfficerDisplay(r.owner_email)}</div>
                                </div>
                                {isCommander && (
                                  <Button 
                                    size="sm" 
                                    className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs"
                                    onClick={() => approveRitualMutation.mutate(r.id)}
                                    disabled={approveRitualMutation.isPending}
                                  >
                                    <CheckCircle className="w-3 h-3 mr-1" /> Approve
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    )}
                  </div>
                </Collapsible>

                {/* Approved Artifacts/Rituals Pending Institutionalization */}
                <Collapsible>
                  <div className={`rounded-lg border ${artifactsPendingInstitutionalization.length + ritualsPendingInstitutionalization.length > 0 ? 'bg-indigo-50 border-indigo-500/30' : 'bg-white border-slate-200'}`}>
                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5 text-indigo-500" />
                        <div className="text-left">
                          <h3 className="text-slate-900 font-semibold">Approved Artifacts/Rituals Pending Institutionalization</h3>
                          <p className="text-xs text-slate-500">Approved items ready to be institutionalized</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={artifactsPendingInstitutionalization.length + ritualsPendingInstitutionalization.length > 0 ? "bg-indigo-500/20 text-indigo-500 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                          {artifactsPendingInstitutionalization.length + ritualsPendingInstitutionalization.length}
                        </Badge>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform`} />
                      </div>
                    </CollapsibleTrigger>
                    {(artifactsPendingInstitutionalization.length + ritualsPendingInstitutionalization.length > 0) && (
                      <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                          {artifactsPendingInstitutionalization.map((a) => (
                            <div key={a.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <div className="flex items-center justify-between">
                                <div className="flex-1 mr-2">
                                  <div className="text-sm font-medium text-slate-900 mb-1">Artifact: {a.name}</div>
                                  <div className="text-xs text-slate-500">Approved by: {getOfficerDisplay(a.approved_by)}</div>
                                </div>
                                {isCommander && (
                                  <Button 
                                    size="sm" 
                                    className="bg-indigo-600 hover:bg-indigo-700 h-7 text-xs"
                                    onClick={() => institutionalizeArtifactMutation.mutate(a.id)}
                                    disabled={institutionalizeArtifactMutation.isPending}
                                  >
                                    <Shield className="w-3 h-3 mr-1" /> Institutionalize
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                          {ritualsPendingInstitutionalization.map((r) => (
                            <div key={r.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <div className="flex items-center justify-between">
                                <div className="flex-1 mr-2">
                                  <div className="text-sm font-medium text-slate-900 mb-1">Ritual: {r.name}</div>
                                  <div className="text-xs text-slate-500">Approved by: {getOfficerDisplay(r.approved_by)}</div>
                                </div>
                                {isCommander && (
                                  <Button 
                                    size="sm" 
                                    className="bg-indigo-600 hover:bg-indigo-700 h-7 text-xs"
                                    onClick={() => institutionalizeRitualMutation.mutate(r.id)}
                                    disabled={institutionalizeRitualMutation.isPending}
                                  >
                                    <Shield className="w-3 h-3 mr-1" /> Institutionalize
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    )}
                  </div>
                </Collapsible>

                {/* Policies/Capabilities Pending Review */}
                {policiesPendingReview.length + capabilitiesRequested.length > 0 &&
              <Collapsible open={expandedSections.policies} onOpenChange={() => toggleSection('policies')}>
                    <div className="rounded-lg border bg-yellow-50 border-amber-500/30">
                      <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-amber-400" />
                          <div className="text-left">
                            <h3 className="text-slate-900 font-semibold">New Policies/Capabilities Pending</h3>
                            <p className="text-xs text-slate-500">Draft policies or requested capabilities</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-amber-500/20 text-amber-400 border-none">
                            {policiesPendingReview.length + capabilitiesRequested.length}
                          </Badge>
                          <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedSections.policies ? 'rotate-90' : ''}`} />
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                          {policiesPendingReview.map((p) => (
                            <div key={p.id} className="p-3 bg-white rounded border border-slate-200">
                              <div className="flex items-center justify-between">
                                <Link to={createPageUrl('issues')} className="flex-1 mr-2">
                                  <div className="text-sm font-medium mb-1">Policy: {p.title}</div>
                                  <div className="text-xs text-slate-500 mb-2">Designed by {getOfficerDisplay(p.designed_by)}</div>
                                </Link>
                                {isCommander && (
                                  <Button 
                                    size="sm" 
                                    className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs"
                                    onClick={() => approvePolicyMutation.mutate(p.id)}
                                    disabled={approvePolicyMutation.isPending}
                                  >
                                    <CheckCircle className="w-3 h-3 mr-1" /> Approve
                                  </Button>
                                )}
                              </div>
                              <ActionItemButtons
                                itemType="policy_pending"
                                itemId={p.id}
                                onDismiss={handleDismiss}
                                onSnooze={handleSnooze}
                              />
                            </div>
                          ))}
                          {capabilitiesRequested.map((c) => (
                            <div key={c.id} className="p-3 bg-white rounded border border-slate-200">
                              <div className="flex items-center justify-between">
                                <Link to={createPageUrl('issues')} className="flex-1 mr-2">
                                  <div className="text-sm font-medium mb-1">Capability: {c.name}</div>
                                  <div className="text-xs text-slate-500 mb-2">Requested by {getOfficerDisplay(c.requested_by)}</div>
                                </Link>
                                {isCommander && (
                                  <Button 
                                    size="sm" 
                                    className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs"
                                    onClick={() => approveCapabilityMutation.mutate(c.id)}
                                    disabled={approveCapabilityMutation.isPending}
                                  >
                                    <CheckCircle className="w-3 h-3 mr-1" /> Approve
                                  </Button>
                                )}
                              </div>
                              <ActionItemButtons
                                itemType="capability_requested"
                                itemId={c.id}
                                onDismiss={handleDismiss}
                                onSnooze={handleSnooze}
                              />
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
              }

                {/* Approved Policies/Capabilities Pending Institutionalization */}
                <Collapsible>
                  <div className={`rounded-lg border ${policiesPendingInstitutionalization.length + capabilitiesPendingInstitutionalization.length > 0 ? 'bg-emerald-50 border-emerald-500/30' : 'bg-white border-slate-200'}`}>
                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <Trophy className="w-5 h-5 text-emerald-500" />
                        <div className="text-left">
                          <h3 className="text-slate-900 font-semibold">Approved Policies/Capabilities Pending Institutionalization</h3>
                          <p className="text-xs text-slate-500">Approved items ready to be institutionalized</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={policiesPendingInstitutionalization.length + capabilitiesPendingInstitutionalization.length > 0 ? "bg-emerald-500/20 text-emerald-500 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                          {policiesPendingInstitutionalization.length + capabilitiesPendingInstitutionalization.length}
                        </Badge>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform`} />
                      </div>
                    </CollapsibleTrigger>
                    {(policiesPendingInstitutionalization.length + capabilitiesPendingInstitutionalization.length > 0) && (
                      <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                          {policiesPendingInstitutionalization.map((p) => (
                            <div key={p.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <div className="flex items-center justify-between">
                                <div className="flex-1 mr-2">
                                  <div className="text-sm font-medium text-slate-900 mb-1">Policy: {p.title}</div>
                                  <div className="text-xs text-slate-500">Approved by: {getOfficerDisplay(p.approved_by)}</div>
                                </div>
                                {isCommander && (
                                  <Button 
                                    size="sm" 
                                    className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs"
                                    onClick={() => institutionalizePolicyMutation.mutate(p.id)}
                                    disabled={institutionalizePolicyMutation.isPending}
                                  >
                                    <Trophy className="w-3 h-3 mr-1" /> Institutionalize
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                          {capabilitiesPendingInstitutionalization.map((c) => (
                            <div key={c.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <div className="flex items-center justify-between">
                                <div className="flex-1 mr-2">
                                  <div className="text-sm font-medium text-slate-900 mb-1">Capability: {c.name}</div>
                                  <div className="text-xs text-slate-500">Approved by: {getOfficerDisplay(c.approved_by)}</div>
                                </div>
                                {isCommander && (
                                  <Button 
                                    size="sm" 
                                    className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs"
                                    onClick={() => institutionalizeCapabilityMutation.mutate(c.id)}
                                    disabled={institutionalizeCapabilityMutation.isPending}
                                  >
                                    <Trophy className="w-3 h-3 mr-1" /> Institutionalize
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    )}
                  </div>
                </Collapsible>

                {/* Aging Unresolved Issues */}
                <Collapsible open={expandedSections.issues} onOpenChange={() => toggleSection('issues')}>
                  <div className={`rounded-lg border ${unresolvedIssues.length > 0 ? 'bg-yellow-50 border-rose-500/30' : 'bg-white border-slate-200'}`}>
                    <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <FileWarning className="w-5 h-5 text-rose-400" />
                        <div className="text-left">
                          <h3 className="text-slate-900 font-semibold">Unresolved Issues</h3>
                          <p className="text-xs text-slate-500">Issues older than 2 weeks or critical</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={unresolvedIssues.length > 0 ? "bg-rose-500/20 text-rose-400 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                          {unresolvedIssues.length}
                        </Badge>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedSections.issues ? 'rotate-90' : ''}`} />
                      </div>
                    </CollapsibleTrigger>
                    {unresolvedIssues.length > 0 && (
                      <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                          {unresolvedIssues.map((issue) => (
                            <div key={issue.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <Link to={createPageUrl('issues')}>
                                <div className="text-sm font-medium text-slate-900 mb-1">{issue.title}</div>
                                <div className="text-xs text-slate-500 mb-2">
                                  {issue.severity} • {issue.status.replace('_', ' ')} • Reported by {getOfficerDisplay(issue.reported_by)}
                                </div>
                              </Link>
                              <ActionItemButtons
                                itemType="unresolved_issue"
                                itemId={issue.id}
                                onDismiss={handleDismiss}
                                onSnooze={handleSnooze}
                              />
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    )}
                  </div>
                </Collapsible>

                {/* Stale Artifacts/Rituals */}
                {staleArtifactsPendingApproval.length + staleRitualsPendingApproval.length > 0 &&
              <Collapsible>
                    <div className="rounded-lg border bg-yellow-50 border-amber-500/30">
                      <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                        <div className="flex items-center gap-3">
                          <Calendar className="w-5 h-5 text-amber-400" />
                          <div className="text-left">
                            <h3 className="text-slate-900 font-semibold">Stale Artifacts/Rituals</h3>
                            <p className="text-xs text-slate-500">Awaiting approval for 2+ weeks</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-amber-500/20 text-amber-400 border-none">
                            {staleArtifactsPendingApproval.length + staleRitualsPendingApproval.length}
                          </Badge>
                          <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform`} />
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                          {staleArtifactsPendingApproval.map((a) => (
                            <div key={a.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <Link to={createPageUrl('artifacts')}>
                                <div className="text-sm font-medium text-slate-900 mb-1">Artifact: {a.name}</div>
                                <div className="text-xs text-slate-500 mb-2">Owner: {getOfficerDisplay(a.owner_email)}</div>
                              </Link>
                              <ActionItemButtons
                                itemType="stale_artifact"
                                itemId={a.id}
                                onDismiss={handleDismiss}
                                onSnooze={handleSnooze}
                              />
                            </div>
                          ))}
                          {staleRitualsPendingApproval.map((r) => (
                            <div key={r.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <Link to={createPageUrl('artifacts')}>
                                <div className="text-sm font-medium text-slate-900 mb-1">Ritual: {r.name}</div>
                                <div className="text-xs text-slate-500 mb-2">Owner: {getOfficerDisplay(r.owner_email)}</div>
                              </Link>
                              <ActionItemButtons
                                itemType="stale_ritual"
                                itemId={r.id}
                                onDismiss={handleDismiss}
                                onSnooze={handleSnooze}
                              />
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
              }
                
                {/* Training Requests */}
                {pendingTrainingRequests.length > 0 &&
              <Collapsible>
                    <div className="rounded-lg border bg-yellow-50 border-cyan-500/30">
                      <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                        <div className="flex items-center gap-3">
                          <TrendingUp className="w-5 h-5 text-cyan-400" />
                          <div className="text-left">
                            <h3 className="text-slate-900 font-semibold">Training Requests</h3>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-cyan-500/20 text-cyan-400 border-none">
                            {pendingTrainingRequests.length}
                          </Badge>
                          <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform`} />
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                          {pendingTrainingRequests.map((tr) => (
                            <div key={tr.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                              <Link to={createPageUrl('profile')}>
                                <div className="text-sm font-medium text-slate-900 mb-1">{tr.skill_name} (Target Lvl {tr.target_level})</div>
                                <div className="text-xs text-slate-500 mb-2">Request by {getOfficerDisplay(tr.officer_email)}</div>
                              </Link>
                              <ActionItemButtons
                                itemType="training_request"
                                itemId={tr.id}
                                onDismiss={handleDismiss}
                                onSnooze={handleSnooze}
                              />
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                  }
                  </div>
                  </CollapsibleContent>
                  </Collapsible>

            {/* VI. Bots Management */}
            <BotsManagementSection
              currentUser={user}
              officers={officers}
              dailyTasks={dailyTasks}
              activities={activities}
              getOfficerDisplay={getOfficerDisplay}
            />

                  </CardContent>
      </Card>

      {/* High Council System Health */}
      <Collapsible>
        <Card className="glass-panel border-none">
          <CollapsibleTrigger className="w-full">
            <CardHeader className="cursor-pointer hover:bg-slate-900/30 transition-colors rounded-t-lg">
              <div className="flex items-center justify-between">
                <CardTitle className="text-slate-900 font-semibold tracking-tight leading-none flex items-center gap-2">
                  <Shield className="w-5 h-5 text-indigo-400" />
                  High Council — System Health
                </CardTitle>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
  <CardContent className="space-y-4">
    <HighCouncilMetrics
      issues={issues}
      policies={policies}
      capabilities={capabilities}
      officers={officers}
      councilSessions={councilSessions}
    />
    <AntiPatternDetector />
  </CardContent>
</CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Second Row - Action Items */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card
          className="glass-panel border-none overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => setShowActionItemsDialog(true)}>

          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-rose-400" />
                <CardTitle className="text-slate-700 text-lg font-semibold tracking-tight">Action Items</CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {actionItemsByOfficer.
              sort((a, b) => b.count - a.count).
              slice(0, 10).
              map((item) => {
                const officer = officers.find((o) => o.user_email === item.email);
                const maxCount = Math.max(...actionItemsByOfficer.map((i) => i.count));
                return (
                  <div key={item.email} className="flex items-center gap-2">
                      <div className="w-24 text-xs text-slate-600 truncate">
                        {officer?.callsign || item.email.split('@')[0]}
                      </div>
                      <div className="flex-1 relative">
                        <div className="h-6 bg-slate-200 rounded overflow-hidden">
                          <div
                          className="h-full bg-rose-500 flex items-center justify-center text-white font-bold text-xs"
                          style={{ width: `${item.count / maxCount * 100}%`, minWidth: '24px' }}>

                            {item.count}
                          </div>
                        </div>
                      </div>
                    </div>);

              })}
              {actionItemsByOfficer.length > 10 &&
              <div className="flex items-center gap-2">
                  <div className="w-24 text-xs text-slate-600">Others</div>
                  <div className="flex-1 relative">
                    <div className="h-6 bg-slate-200 rounded overflow-hidden">
                      <div
                      className="h-full bg-slate-400 flex items-center justify-center text-white font-bold text-xs"
                      style={{
                        width: `${actionItemsByOfficer.slice(10).reduce((sum, i) => sum + i.count, 0) / Math.max(...actionItemsByOfficer.map((i) => i.count)) * 100}%`,
                        minWidth: '24px'
                      }}>

                        {actionItemsByOfficer.slice(10).reduce((sum, i) => sum + i.count, 0)}
                      </div>
                    </div>
                  </div>
                </div>
              }
            </div>
          </CardContent>
        </Card>

        <IssueTagsCard
          reportIssues={reportIssues}
          progressReports={progressReports}
          activities={activities}
          artifacts={artifacts}
          rituals={rituals}
          policies={policies}
          capabilities={capabilities}
          onTagClick={(tag) => setTagHistoryDialog({ open: true, tag })}
          officers={officers} />

      </div>

      {/* Issues by Hierarchy Card */}
      <IssuesByHierarchyCard
        issues={issues}
        missions={missions}
        objectives={allObjectives}
        activities={activities}
      />

      {/* Third Row - Strategy/Tactics, Values */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card
          className="glass-panel border-none overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => setShowStrategyTacticsDialog(true)}>

          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-purple-400" />
                <CardTitle className="text-slate-700 text-lg font-semibold tracking-tight">Strategy/Tactics</CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {strategyTacticsData.length === 0 ?
              <div className="text-center py-4 text-slate-500 text-sm">
                  No strategy or tactics nodes
                </div> :

              <>
                  {strategyTacticsData.slice(0, 5).map(({ node, xp, missionsByFlag }) => {
                  const maxXP = Math.max(...strategyTacticsData.map((d) => d.xp));
                  const totalMissions = missionsByFlag.green + missionsByFlag.yellow + missionsByFlag.red;

                  return (
                    <div key={node.id} className="flex items-center gap-2">
                        <div className="w-32 text-xs text-slate-600 truncate">
                          {node.title}
                        </div>
                        <div className="flex-1 relative">
                          <div className="h-6 bg-slate-200 rounded overflow-hidden flex">
                            {missionsByFlag.green > 0 &&
                          <div
                            className="h-full bg-emerald-500 flex items-center justify-center text-white font-bold text-xs"
                            style={{ width: `${missionsByFlag.green / totalMissions * (xp / maxXP * 100)}%`, minWidth: '20px' }}>

                                {missionsByFlag.green}
                              </div>
                          }
                            {missionsByFlag.yellow > 0 &&
                          <div
                            className="h-full bg-amber-500 flex items-center justify-center text-white font-bold text-xs"
                            style={{ width: `${missionsByFlag.yellow / totalMissions * (xp / maxXP * 100)}%`, minWidth: '20px' }}>

                                {missionsByFlag.yellow}
                              </div>
                          }
                            {missionsByFlag.red > 0 &&
                          <div
                            className="h-full bg-red-500 flex items-center justify-center text-white font-bold text-xs"
                            style={{ width: `${missionsByFlag.red / totalMissions * (xp / maxXP * 100)}%`, minWidth: '20px' }}>

                                {missionsByFlag.red}
                              </div>
                          }
                          </div>
                        </div>
                        <div className="text-xs text-slate-600 font-semibold w-16 text-right">
                          {xp} XP
                        </div>
                      </div>);

                })}
                  {strategyTacticsData.length > 5 &&
                <div className="flex items-center gap-2">
                      <div className="w-32 text-xs text-slate-600">Others</div>
                      <div className="flex-1 relative">
                        <div className="h-6 bg-slate-200 rounded overflow-hidden flex">
                          {(() => {
                        const others = strategyTacticsData.slice(5);
                        const othersXP = others.reduce((sum, d) => sum + d.xp, 0);
                        const othersGreen = others.reduce((sum, d) => sum + d.missionsByFlag.green, 0);
                        const othersYellow = others.reduce((sum, d) => sum + d.missionsByFlag.yellow, 0);
                        const othersRed = others.reduce((sum, d) => sum + d.missionsByFlag.red, 0);
                        const othersTotal = othersGreen + othersYellow + othersRed;
                        const maxXP = Math.max(...strategyTacticsData.map((d) => d.xp));

                        return (
                          <>
                                {othersGreen > 0 &&
                            <div
                              className="h-full bg-emerald-500 flex items-center justify-center text-white font-bold text-xs"
                              style={{ width: `${othersGreen / othersTotal * (othersXP / maxXP * 100)}%`, minWidth: '20px' }}>

                                    {othersGreen}
                                  </div>
                            }
                                {othersYellow > 0 &&
                            <div
                              className="h-full bg-amber-500 flex items-center justify-center text-white font-bold text-xs"
                              style={{ width: `${othersYellow / othersTotal * (othersXP / maxXP * 100)}%`, minWidth: '20px' }}>

                                    {othersYellow}
                                  </div>
                            }
                                {othersRed > 0 &&
                            <div
                              className="h-full bg-red-500 flex items-center justify-center text-white font-bold text-xs"
                              style={{ width: `${othersRed / othersTotal * (othersXP / maxXP * 100)}%`, minWidth: '20px' }}>

                                    {othersRed}
                                  </div>
                            }
                              </>);

                      })()}
                        </div>
                      </div>
                      <div className="text-xs text-slate-600 font-semibold w-16 text-right">
                        {strategyTacticsData.slice(5).reduce((sum, d) => sum + d.xp, 0)} XP
                      </div>
                    </div>
                }
                </>
              }
            </div>
          </CardContent>
        </Card>

        <Card
          className="glass-panel border-none overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => setShowStrugglingPrinciplesDialog(true)}>

          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-indigo-400" />
                <CardTitle className="text-slate-700 text-lg font-semibold tracking-tight">Values & Principles</CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {strugglingTopItems.top5.length === 0 ?
              <div className="text-center py-4 text-slate-500 text-sm">
                  No items reported
                </div> :

              <>
                  {strugglingTopItems.top5.map(({ item, struggling, total }) => {
                  const isRedLine = immutables.some((imm) =>
                  imm.statement === item &&
                  ['company_red_line', 'people_red_line'].includes(imm.category)
                  );
                  const maxCount = Math.max(...strugglingTopItems.top5.map((i) => i.total), strugglingTopItems.othersTotalTotal);
                  const strugglingPercent = struggling / maxCount * 100;
                  const nonStrugglingPercent = (total - struggling) / maxCount * 100;

                  return (
                    <div key={item} className="flex items-center gap-2">
                        <div className="w-32 text-xs text-slate-600 truncate flex items-center gap-1">
                          {isRedLine ?
                        <Flame className="w-3 h-3 text-rose-400 flex-shrink-0" /> :

                        <Shield className="w-3 h-3 text-indigo-400 flex-shrink-0" />
                        }
                          <span className="truncate">{item}</span>
                        </div>
                        <div className="flex-1 relative">
                          <div className="h-6 bg-slate-200 rounded overflow-hidden flex">
                            <div
                            className="h-full bg-red-500 flex items-center justify-center text-white font-bold text-xs"
                            style={{ width: `${strugglingPercent}%`, minWidth: struggling > 0 ? '20px' : '0' }}>

                              {struggling > 0 ? struggling : ''}
                            </div>
                            <div
                            className="h-full bg-indigo-500 flex items-center justify-center text-white font-bold text-xs"
                            style={{ width: `${nonStrugglingPercent}%`, minWidth: total - struggling > 0 ? '20px' : '0' }}>

                              {total - struggling > 0 ? total - struggling : ''}
                            </div>
                          </div>
                        </div>
                      </div>);

                })}
                  {strugglingTopItems.othersTotalTotal > 0 &&
                <div className="flex items-center gap-2">
                      <div className="w-32 text-xs text-slate-600">Others</div>
                      <div className="flex-1 relative">
                        <div className="h-6 bg-slate-200 rounded overflow-hidden flex">
                          <div
                        className="h-full bg-red-500 flex items-center justify-center text-white font-bold text-xs"
                        style={{
                          width: `${strugglingTopItems.othersStrugglingTotal / Math.max(...strugglingTopItems.top5.map((i) => i.total), strugglingTopItems.othersTotalTotal) * 100}%`,
                          minWidth: strugglingTopItems.othersStrugglingTotal > 0 ? '20px' : '0'
                        }}>

                            {strugglingTopItems.othersStrugglingTotal > 0 ? strugglingTopItems.othersStrugglingTotal : ''}
                          </div>
                          <div
                        className="h-full bg-indigo-500 flex items-center justify-center text-white font-bold text-xs"
                        style={{
                          width: `${(strugglingTopItems.othersTotalTotal - strugglingTopItems.othersStrugglingTotal) / Math.max(...strugglingTopItems.top5.map((i) => i.total), strugglingTopItems.othersTotalTotal) * 100}%`,
                          minWidth: strugglingTopItems.othersTotalTotal - strugglingTopItems.othersStrugglingTotal > 0 ? '20px' : '0'
                        }}>

                            {strugglingTopItems.othersTotalTotal - strugglingTopItems.othersStrugglingTotal > 0 ? strugglingTopItems.othersTotalTotal - strugglingTopItems.othersStrugglingTotal : ''}
                          </div>
                        </div>
                      </div>
                    </div>
                }
                </>
              }
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Overview Metrics */}
      <Collapsible open={expandedSections.missions} onOpenChange={() => toggleSection('missions')}>
        <Card className="glass-panel border-none">
          <CollapsibleTrigger className="w-full">
            <CardHeader className="cursor-pointer hover:bg-slate-900/30 transition-colors rounded-t-lg">
              <div className="flex items-center justify-between">
                <CardTitle className="text-slate-900 font-semibold tracking-tight leading-none flex items-center gap-2">
                  <Target className="w-5 h-5 text-indigo-400" />
                  Mission & Objective Overview
                </CardTitle>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Missions:</span>
                      <span className="ml-2 text-slate-900 font-semibold">{activeMissions.length}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Objectives:</span>
                      <span className="ml-2 text-slate-900 font-semibold">{activeObjectives.length}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Activities:</span>
                      <span className="ml-2 text-slate-900 font-semibold">{activeActivities.length}</span>
                    </div>
                  </div>
                  <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedSections.missions ? 'rotate-90' : ''}`} />
                </div>
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-6">
              {/* Active Missions */}
              {activeMissions.length > 0 &&
              <div>
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Active Missions</h3>
                  <div className="space-y-2">
                    {activeMissions.slice(0, 5).map((mission) =>
                  <Link key={mission.id} to={createPageUrl('missions')}>
                        <div className="bg-white p-3 rounded-lg border border-slate-200 hover:border-indigo-400 hover:shadow-md transition-all flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-1.5 h-10 rounded-full ${
                        mission.game_mode === 'special_ops' ? 'bg-rose-500' :
                        mission.game_mode === 'campaign' ? 'bg-indigo-500' : 'bg-slate-500'}`
                        } />
                            <div>
                              <h4 className="text-slate-900 font-medium text-sm hover:text-indigo-600 transition-colors">{mission.title}</h4>
                              <p className="text-xs text-slate-500">{mission.game_mode.replace('_', ' ').toUpperCase()}</p>
                            </div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-indigo-400" />
                        </div>
                      </Link>
                  )}
                    {activeMissions.length > 5 &&
                  <Link to={createPageUrl('missions')}>
                        <Button size="sm" variant="outline" className="w-full mt-2">
                          View All Missions ({activeMissions.length})
                        </Button>
                      </Link>
                  }
                  </div>
                </div>
              }

              {/* Recent Objectives */}
              {activeObjectives.length > 0 &&
              <div>
                  <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Recent Active Objectives</h3>
                  <div className="space-y-2">
                    {activeObjectives.slice(0, 5).map((obj) => {
                    const mission = missions.find((m) => m.id === obj.mission_id);
                    return (
                      <Link key={obj.id} to={createPageUrl('discovery')}>
                          <div className="bg-white p-3 rounded-lg border border-slate-200 hover:border-cyan-400 hover:shadow-md transition-all">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <div className="text-sm text-slate-900 font-medium line-clamp-2 hover:text-cyan-600 transition-colors">
                                  {obj.objective_code && <span className="text-cyan-500 mr-2">[{obj.objective_code}]</span>}
                                  {obj.statement}
                                </div>
                                {mission && <p className="text-xs text-slate-500 mt-1">{mission.title}</p>}
                              </div>
                              <ArrowRight className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                            </div>
                          </div>
                        </Link>);

                  })}
                    <Link to={createPageUrl('discovery')}>
                      <Button size="sm" variant="outline" className="w-full mt-2">
                        View All Objectives ({activeObjectives.length})
                      </Button>
                    </Link>
                  </div>
                </div>
              }
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Visual Team Map - Commanders Only Viewing Own Dashboard */}
      {isCommander && viewingOwnDashboard &&
      <VisualTeamMap officers={officers} />
      }

      {/* Validation Dialogs */}



      <ProgressReportPrinciplesValidation
        activity={validatingReport.activity}
        report={validatingReport.report}
        open={!!validatingReport.activity}
        onClose={() => setValidatingReport({ activity: null, reportIndex: null })}
        currentUser={user}
        officers={officers} />


      <DailyTaskPrinciplesValidation
        tasks={validatingTasks}
        open={validatingTasks.length > 0}
        onClose={() => setValidatingTasks([])}
        currentUser={user}
        officers={officers} />

      <ActionItemsDialog
        open={showActionItemsDialog}
        onClose={() => setShowActionItemsDialog(false)}
        officers={officers}
        actionItemsByOfficer={actionItemsByOfficer} />

      <OpenIssuesDialog
        open={showOpenIssuesDialog}
        onClose={() => setShowOpenIssuesDialog(false)}
        officers={officers}
        issuesByOfficer={issuesByOfficer} />

      <StrugglingPrinciplesDialog
        open={showStrugglingPrinciplesDialog}
        onClose={() => setShowStrugglingPrinciplesDialog(false)}
        principlesStats={principlesStats}
        immutables={immutables}
        progressReports={progressReports}
        activities={activities}
        officers={officers} />



      <StrategyTacticsDialog
        open={showStrategyTacticsDialog}
        onClose={() => setShowStrategyTacticsDialog(false)}
        strategyTacticsData={strategyTacticsData}
        missions={missions} />

      <TagHistoryDialog
        open={tagHistoryDialog.open}
        onClose={() => setTagHistoryDialog({ open: false, tag: null })}
        tag={tagHistoryDialog.tag}
        reportIssues={reportIssues}
        progressReports={progressReports}
        officers={officers}
        activities={activities} />


    </div>);

}